import { Download, Pen, Eraser, Trash2, Undo, Redo, Plus, AlertCircle, Share2, Users, Edit3, X, ChevronLeft, ChevronRight, Loader2, FolderOpen, Save, Maximize2, Minimize2, ZoomIn, ZoomOut, Hand } from 'lucide-react';
import WhiteboardShareModal, { WhiteboardShareFormData } from '../../components/modals/WhiteboardShareModal';
import WhiteboardParticipantsModal from '../../components/modals/WhiteboardParticipantsModal';
import TabPage, { TabItemProps } from '../../components/dynamic/Tab';
import PdfSelectModal from '../../components/modals/PdfSelectModal';
import React, { useState, useRef, useEffect } from 'react';
import checkPermission from '../../utils/permission';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../hooks/useModal';
import { ApiService } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import io, { Socket } from 'socket.io-client';
interface PdfPage {
  pageNumber: number;
  imageUrl: string;
  width: number;
  height: number;
}

interface WhiteboardData {
  id?: string;
  name?: string;
  canvasData?: ImageData;
  history: ImageData[];
  historyStep: number;
  isOwner?: boolean;
  isShared?: boolean;
  sharedBy?: any;
  myPermissions?: { canDraw: boolean; canErase?: boolean };
  isSharingStopped?: boolean;
  stoppedByOwnerName?: string;
  pdfPages?: PdfPage[];
  currentPdfPage?: number;
  pdfFileId?: string;
  pageDrawings?: { [pageNumber: number]: string };
}

interface DrawingData {
  x: number;
  y: number;
  color: string;
  size: number;
  tool: "pen" | "eraser";
  type: "start" | "draw" | "end";
  coordinateSpace?: "canvas" | "pdf";
  normalizedX?: number;
  normalizedY?: number;
}

interface InkAnalysis {
  hasMeaningfulInk: boolean;
  isBlankOrWhite: boolean;
}

interface CompactStrokeTransport {
  f: "s2";
  t: "pen" | "eraser";
  c: string;
  s: number;
  cs: "canvas" | "pdf";
  p: Array<[number, number]>;
}

const rightSidebarButtonBase =
  "w-[48px] h-[48px] flex items-center justify-center rounded-2xl transition-all duration-300";
const MAX_HISTORY_ENTRIES = 12;
const MIN_POINT_DISTANCE_PX = 1.5;

const WhiteboardCanvas = ({
  tabId,
  whiteboardId,
  onAddNew,
  onDelete,
  canDelete,
  whiteboardData,
  onDataChange,
  totalTabs,
  isOwner,
  isShared,
  sharedBy,
  myPermissions,
  onShare,
  onViewParticipants,
  onStopSharing,
  onCloseTab,
  isSharingStopped,
  stoppedByOwnerName,
  socket,
  onCanvasRef,
  onFullscreenContainerChange,
  onToggleFullscreen,
  isFullscreen = false,
  fullscreenContainer = null,
}: {
  tabId: string;
  whiteboardId?: string;
  onAddNew: () => void;
  onDelete: () => void;
  canDelete: boolean;
  whiteboardData: WhiteboardData;
  onDataChange: (data: WhiteboardData) => void;
  totalTabs: number;
  isOwner: boolean;
  isShared: boolean;
  sharedBy?: any;
  myPermissions: { canDraw: boolean; canErase?: boolean };
  onShare: () => void;
  onViewParticipants: () => void;
  onStopSharing: () => void;
  onCloseTab: () => void;
  isSharingStopped?: boolean;
  stoppedByOwnerName?: string;
  socket: Socket | null;
  onCanvasRef?: (tabId: string, canvas: HTMLCanvasElement | null) => void;
  onFullscreenContainerChange?: (container: HTMLElement | null) => void;
  onToggleFullscreen?: () => void;
  isFullscreen?: boolean;
  fullscreenContainer?: HTMLElement | null;
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const setCanvasRef = (canvas: HTMLCanvasElement | null) => {
    canvasRef.current = canvas;
    if (onCanvasRef) {
      onCanvasRef(tabId, canvas);
    }
  };
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingTool, setDrawingTool] = useState<"pen" | "eraser" | "hand">("pen");
  const [penSize, setPenSize] = useState(3);
  const [eraserSize, setEraserSize] = useState(20);
  const [penColor, setPenColor] = useState("#1a1a2e");
  const [panOffset, setPanOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const panOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const isPanningRef = useRef(false);
  const lastPanPointRef = useRef<{ x: number; y: number } | null>(null);

  const [lastActionMessage, setLastActionMessage] = useState<string>("");
  const actionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [isBlocked, setIsBlocked] = useState(false);
  const [blockMessage, setBlockMessage] = useState("");
  const [isClearingUI, setIsClearingUI] = useState(false);
  const [pdfBackgroundImage, setPdfBackgroundImage] = useState<HTMLImageElement | null>(null);
  const pdfBackgroundImageRef = useRef<HTMLImageElement | null>(null);
  pdfBackgroundImageRef.current = pdfBackgroundImage;
  const [isPdfSelectModalOpen, setIsPdfSelectModalOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomOrigin, setZoomOrigin] = useState<{ x: string; y: string }>({ x: '50%', y: '50%' });
  const zoomLevelRef = useRef(1);
  zoomLevelRef.current = zoomLevel;
  const baseZoomLevelRef = useRef(1);
  const basePanOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const canvasWrapperRef = useRef<HTMLDivElement | null>(null);
  const [activeMenu, setActiveMenu] = useState<'pen' | 'eraser' | 'color' | null>(null);
  const [pdfPageInput, setPdfPageInput] = useState(String(whiteboardData.currentPdfPage || 1));

  const eraserCursorRef = useRef<HTMLDivElement | null>(null);
  const containerRectCache = useRef<DOMRect | null>(null);
  const blockTimeout = useRef<NodeJS.Timeout | null>(null);
  const pendingSnapshotRef = useRef<string | null>(null);
  const pendingJoinStrokesRef = useRef<DrawingData[][]>([]);
  const pendingSnapshotVersionRef = useRef(0);
  const preStrokeImageDataRef = useRef<ImageData | null>(null);
  const [pdfRenderBounds, setPdfRenderBounds] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const latestPdfLoadKeyRef = useRef<string | null>(null);
  const appliedPdfLoadKeyRef = useRef<string | null>(null);

  const getReferenceBounds = (
    width: number,
    height: number,
    pdfImg?: HTMLImageElement | null
  ) => {
    if (!pdfImg || !pdfImg.naturalWidth || !pdfImg.naturalHeight) {
      return { x: 0, y: 0, width, height };
    }

    const scale = Math.min(width / pdfImg.naturalWidth, height / pdfImg.naturalHeight);
    const renderedWidth = pdfImg.naturalWidth * scale;
    const renderedHeight = pdfImg.naturalHeight * scale;

    return {
      x: (width - renderedWidth) / 2,
      y: (height - renderedHeight) / 2,
      width: renderedWidth,
      height: renderedHeight,
    };
  };

  const getCurrentReferenceBounds = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return { x: 0, y: 0, width: 1, height: 1 };
    }

    const dpr = window.devicePixelRatio || 1;
    const width = canvas.width / dpr;
    const height = canvas.height / dpr;

    return getReferenceBounds(width, height, pdfBackgroundImageRef.current);
  };

  const syncPdfRenderBounds = (canvas?: HTMLCanvasElement | null, pdfImg?: HTMLImageElement | null) => {
    const activeCanvas = canvas || canvasRef.current;
    const activePdf = pdfImg || pdfBackgroundImageRef.current;

    if (!activeCanvas || !activePdf) {
      setPdfRenderBounds(null);
      return;
    }

    const dpr = window.devicePixelRatio || 1;
    const width = activeCanvas.width / dpr;
    const height = activeCanvas.height / dpr;
    setPdfRenderBounds(getReferenceBounds(width, height, activePdf));
  };

  const toCanvasPoint = (drawing: DrawingData) => {
    const bounds = getCurrentReferenceBounds();

    if (
      drawing.normalizedX !== undefined &&
      drawing.normalizedY !== undefined
    ) {
      const coordinateSpace = drawing.coordinateSpace || "canvas";

      if (coordinateSpace === "pdf") {
        return {
          x: bounds.x + drawing.normalizedX * bounds.width,
          y: bounds.y + drawing.normalizedY * bounds.height,
        };
      }

      return {
        x: drawing.normalizedX * bounds.width,
        y: drawing.normalizedY * bounds.height,
      };
    }

    return { x: drawing.x, y: drawing.y };
  };

  const createDrawingPoint = (
    x: number,
    y: number,
    tool: "pen" | "eraser",
    type: "start" | "draw" | "end"
  ): DrawingData => {
    const bounds = getCurrentReferenceBounds();
    const isPdfMode = Boolean(pdfBackgroundImageRef.current || whiteboardDataRef.current.pdfPages);
    const referenceWidth = isPdfMode ? bounds.width : Math.max(bounds.width, 1);
    const referenceHeight = isPdfMode ? bounds.height : Math.max(bounds.height, 1);
    const referenceX = isPdfMode ? bounds.x : 0;
    const referenceY = isPdfMode ? bounds.y : 0;

    return {
      x,
      y,
      color: penColorRef.current,
      size: tool === "pen" ? penSizeRef.current : eraserSizeRef.current,
      tool,
      type,
      coordinateSpace: isPdfMode ? "pdf" : "canvas",
      normalizedX: (x - referenceX) / referenceWidth,
      normalizedY: (y - referenceY) / referenceHeight,
    };
  };

  const roundNumber = (value: number | undefined, precision: number) => {
    if (value === undefined || !Number.isFinite(value)) return value;
    const factor = 10 ** precision;
    return Math.round(value * factor) / factor;
  };

  const sanitizeDrawingPointForTransport = (point: DrawingData): DrawingData => ({
    ...point,
    x: roundNumber(point.x, 1) || 0,
    y: roundNumber(point.y, 1) || 0,
    normalizedX: roundNumber(point.normalizedX, 4),
    normalizedY: roundNumber(point.normalizedY, 4),
  });

  const buildCompactStrokeTransport = (
    stroke: DrawingData[]
  ): CompactStrokeTransport | null => {
    if (!Array.isArray(stroke) || stroke.length === 0) return null;

    const sanitizedStroke = stroke.map(sanitizeDrawingPointForTransport);
    const firstPoint = sanitizedStroke[0];

    return {
      f: "s2",
      t: firstPoint.tool,
      c: firstPoint.color,
      s: firstPoint.size,
      cs: firstPoint.coordinateSpace || "canvas",
      p: sanitizedStroke.map((point) => [
        point.normalizedX ?? point.x,
        point.normalizedY ?? point.y,
      ]),
    };
  };

  const isCompactStrokeTransport = (payload: any): payload is CompactStrokeTransport =>
    Boolean(
      payload &&
      typeof payload === "object" &&
      payload.f === "s2" &&
      Array.isArray(payload.p)
    );

  const expandCompactStrokeTransport = (payload: CompactStrokeTransport): DrawingData[] =>
    payload.p.map(([normalizedX, normalizedY], index) => ({
      x: normalizedX,
      y: normalizedY,
      color: payload.c,
      size: payload.s,
      tool: payload.t,
      type: index === 0 ? "start" : "draw",
      coordinateSpace: payload.cs,
      normalizedX,
      normalizedY,
    }));

  const normalizeStrokePayload = (payload: any): DrawingData[] => {
    if (isCompactStrokeTransport(payload)) {
      return expandCompactStrokeTransport(payload);
    }

    return Array.isArray(payload) ? payload : [];
  };

  const drawSnapshotOnCanvas = (
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    img: HTMLImageElement
  ) => {
    const dpr = window.devicePixelRatio || 1;
    const canvasWidth = canvas.width / dpr;
    const canvasHeight = canvas.height / dpr;
    const activePdfImage = pdfBackgroundImageRef.current;

    if (activePdfImage) {
      const sourceBounds = getReferenceBounds(img.naturalWidth, img.naturalHeight, activePdfImage);
      const targetBounds = getReferenceBounds(canvasWidth, canvasHeight, activePdfImage);

      ctx.drawImage(
        img,
        sourceBounds.x,
        sourceBounds.y,
        sourceBounds.width,
        sourceBounds.height,
        targetBounds.x,
        targetBounds.y,
        targetBounds.width,
        targetBounds.height
      );
      return;
    }

    ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
  };

  const extractStrokeHistory = (drawings: any[]): DrawingData[][] => {
    if (!Array.isArray(drawings)) return [];

    return drawings.filter((item) => {
      if (!Array.isArray(item) || item.length < 2) return false;
      const firstPoint = item[0];
      return Boolean(
        firstPoint &&
          typeof firstPoint === "object" &&
          (firstPoint.type === "start" || firstPoint.type === "draw")
      );
    });
  };

  const renderPersistedJoinStrokes = (ctx: CanvasRenderingContext2D) => {
    const strokes = pendingJoinStrokesRef.current;
    if (!Array.isArray(strokes) || strokes.length === 0) return;

    const previousCompositeOperation = ctx.globalCompositeOperation;

    strokes.forEach((stroke) => {
      if (!Array.isArray(stroke) || stroke.length < 2) return;

      const firstPoint = stroke[0];
      const firstResolved = toCanvasPoint(firstPoint);

      ctx.beginPath();
      ctx.moveTo(firstResolved.x, firstResolved.y);

      for (let i = 1; i < stroke.length; i++) {
        const point = stroke[i];
        const resolvedPoint = toCanvasPoint(point);
        ctx.lineTo(resolvedPoint.x, resolvedPoint.y);
      }

      if (firstPoint.tool === "pen") {
        ctx.globalCompositeOperation = "source-over";
        ctx.strokeStyle = firstPoint.color;
        ctx.lineWidth = firstPoint.size;
      } else {
        ctx.globalCompositeOperation = "destination-out";
        ctx.lineWidth = firstPoint.size;
      }
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.stroke();
      ctx.closePath();
    });

    ctx.globalCompositeOperation = previousCompositeOperation;
    pendingJoinStrokesRef.current = [];
  };

  const remoteDrawingPaths = useRef<Map<string, { x: number; y: number; isDrawing: boolean }>>(new Map());
  const remoteRenderedPointCounts = useRef<Map<string, number>>(new Map());
  const isClearingRef = useRef(false);
  const currentStroke = useRef<DrawingData[]>([]);
  const outgoingDrawQueueRef = useRef<DrawingData[]>([]);
  const outgoingDrawFrameRef = useRef<number | null>(null);

  const isPageLoading = useRef(false);
  const drawingBuffer = useRef<{ drawing: DrawingData; userId: string }[]>([]);
  const pageDrawingsRef = useRef<{ [pageNumber: number]: string }>({});
  const whiteboardDataRef = useRef(whiteboardData);
  whiteboardDataRef.current = whiteboardData;
  const savedCanvasDataRef = useRef<string | null>(null);

  const isDrawingRef = useRef(isDrawing);
  isDrawingRef.current = isDrawing;
  const penColorRef = useRef(penColor);
  penColorRef.current = penColor;
  const penSizeRef = useRef(penSize);
  penSizeRef.current = penSize;
  const eraserSizeRef = useRef(eraserSize);
  eraserSizeRef.current = eraserSize;
  const drawingToolRef = useRef(drawingTool);
  drawingToolRef.current = drawingTool;
  panOffsetRef.current = panOffset;
  const { user } = useAuth();
  const navigate = useNavigate();

  const canDraw = isOwner || myPermissions.canDraw;
  const canErase = isOwner || myPermissions.canErase;

  const emitImmediateDrawing = (drawing: DrawingData) => {
    if (!socket || !whiteboardId) return;

    socket.emit("draw", {
      whiteboardId,
      userId: user?.id,
      userName: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
      drawing: sanitizeDrawingPointForTransport(drawing),
    });
  };

  const flushQueuedDrawings = () => {
    if (outgoingDrawFrameRef.current !== null) {
      window.cancelAnimationFrame(outgoingDrawFrameRef.current);
      outgoingDrawFrameRef.current = null;
    }

    if (!socket || !whiteboardId) {
      outgoingDrawQueueRef.current = [];
      return;
    }

    const drawings = outgoingDrawQueueRef.current.splice(0, outgoingDrawQueueRef.current.length);
    if (drawings.length === 0) return;

    socket.emit("draw-batch", {
      whiteboardId,
      userId: user?.id,
      drawings: drawings.map(sanitizeDrawingPointForTransport),
    });
  };

  const queueDrawingForBroadcast = (drawing: DrawingData) => {
    outgoingDrawQueueRef.current.push(drawing);

    if (outgoingDrawFrameRef.current !== null) {
      return;
    }

    outgoingDrawFrameRef.current = window.requestAnimationFrame(() => {
      outgoingDrawFrameRef.current = null;
      flushQueuedDrawings();
    });
  };

  const resetTransientDrawingState = () => {
    remoteDrawingPaths.current.clear();
    remoteRenderedPointCounts.current.clear();
    drawingBuffer.current = [];
    pageDrawingsRef.current = {};
    currentStroke.current = [];
    pendingSnapshotRef.current = null;
    pendingJoinStrokesRef.current = [];
    pendingSnapshotVersionRef.current += 1;
    savedCanvasDataRef.current = null;
    preStrokeImageDataRef.current = null;
    isPageLoading.current = false;

    if (outgoingDrawFrameRef.current !== null) {
      window.cancelAnimationFrame(outgoingDrawFrameRef.current);
      outgoingDrawFrameRef.current = null;
    }

    outgoingDrawQueueRef.current = [];
  };



  useEffect(() => {
    if (canDraw) {
      setDrawingTool("pen");
    }
  }, [canDraw]);

  useEffect(() => {
    setPdfPageInput(String(whiteboardData.currentPdfPage || 1));
  }, [whiteboardData.currentPdfPage, whiteboardData.pdfPages?.length]);

  const colorOptions = [
    { name: "Siyah", value: "#1a1a2e" },
    { name: "Kırmızı", value: "#e63946" },
    { name: "Mavi", value: "#0077b6" },
    { name: "Yeşil", value: "#2a9d8f" },
    { name: "Turuncu", value: "#f4a261" },
    { name: "Mor", value: "#7209b7" },
    { name: "Sarı", value: "#fca311" },
    { name: "Pembe", value: "#e91e63" },
  ];

  const analyzePixelInk = (
    pixels: Uint8ClampedArray,
    width: number,
    height: number,
    pdfMode: boolean
  ): InkAnalysis => {
    if (!pdfMode) {
      return { hasMeaningfulInk: true, isBlankOrWhite: false };
    }

    const stride = 4;
    const alphaThreshold = 10;
    const whiteThreshold = 245;
    const minMeaningfulPixels = 8;
    const minMeaningfulRatio = 0.003;

    let sampled = 0;
    let nonTransparent = 0;
    let meaningful = 0;

    for (let y = 0; y < height; y += stride) {
      for (let x = 0; x < width; x += stride) {
        sampled += 1;
        const idx = (y * width + x) * 4;
        const r = pixels[idx];
        const g = pixels[idx + 1];
        const b = pixels[idx + 2];
        const a = pixels[idx + 3];

        if (a <= alphaThreshold) continue;
        nonTransparent += 1;

        const isNearWhite =
          r >= whiteThreshold && g >= whiteThreshold && b >= whiteThreshold;

        if (!isNearWhite) {
          meaningful += 1;
        }
      }
    }

    const meaningfulRatio = meaningful / Math.max(nonTransparent, 1);
    const hasMeaningfulInk =
      meaningful >= minMeaningfulPixels || meaningfulRatio >= minMeaningfulRatio;

    const isBlankOrWhite = !hasMeaningfulInk;
    return { hasMeaningfulInk, isBlankOrWhite };
  };

  const analyzeCanvasInk = (
    canvas: HTMLCanvasElement,
    pdfMode: boolean
  ): InkAnalysis => {
    if (!pdfMode) {
      return { hasMeaningfulInk: true, isBlankOrWhite: false };
    }

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx || canvas.width === 0 || canvas.height === 0) {
      return { hasMeaningfulInk: false, isBlankOrWhite: true };
    }

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return analyzePixelInk(imageData.data, canvas.width, canvas.height, true);
  };

  const analyzeDataUrlInk = async (
    dataUrl: string,
    pdfMode: boolean
  ): Promise<InkAnalysis> => {
    if (!dataUrl || !pdfMode) {
      return { hasMeaningfulInk: true, isBlankOrWhite: false };
    }

    return new Promise((resolve) => {
      const img = new Image();

      img.onload = () => {
        try {
          const tempCanvas = document.createElement("canvas");
          const width = img.naturalWidth || img.width;
          const height = img.naturalHeight || img.height;
          tempCanvas.width = width;
          tempCanvas.height = height;

          const tempCtx = tempCanvas.getContext("2d", { willReadFrequently: true });
          if (!tempCtx || width === 0 || height === 0) {
            resolve({ hasMeaningfulInk: false, isBlankOrWhite: true });
            return;
          }

          tempCtx.drawImage(img, 0, 0, width, height);
          const imageData = tempCtx.getImageData(0, 0, width, height);
          resolve(analyzePixelInk(imageData.data, width, height, true));
        } catch (error) {
          resolve({ hasMeaningfulInk: true, isBlankOrWhite: false });
        }
      };

      img.onerror = () => {
        resolve({ hasMeaningfulInk: true, isBlankOrWhite: false });
      };

      img.src = dataUrl;
    });
  };

  const updateActionMessage = (userName: string, tool: "pen" | "eraser") => {
    if (actionTimeoutRef.current) {
      clearTimeout(actionTimeoutRef.current);
    }

    const toolName = tool === "pen" ? "Kalem" : "Silgi";
    const message = `${userName} ${toolName} Kullandı`;

    setLastActionMessage(message);

    actionTimeoutRef.current = setTimeout(() => {
      setLastActionMessage("");
    }, 5000);
  };

  useEffect(() => {
    if (!socket || !whiteboardId) {
      return;
    }

    socket.emit("join-whiteboard", {
      whiteboardId,
      userId: user?.id,
      userName: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
    });

    socket.on("whiteboard-state", async (data: { drawings: any[]; whiteboardId: string; pdfFileId?: string; pdfPages?: PdfPage[]; currentPdfPage?: number }) => {
      const snapshotVersion = pendingSnapshotVersionRef.current + 1;
      pendingSnapshotVersionRef.current = snapshotVersion;

      if (data.pdfPages && data.pdfPages.length > 0) {
        const currentPage = data.currentPdfPage || 1;
        const latestSnapshot =
          Array.isArray(data.drawings)
            ? [...data.drawings].reverse().find((item) => typeof item === "string") || null
            : null;

        pendingSnapshotRef.current = null;
        pendingJoinStrokesRef.current = extractStrokeHistory(data.drawings || []);
        if (latestSnapshot) {
          const analysis = await analyzeDataUrlInk(latestSnapshot, true);
          if (pendingSnapshotVersionRef.current !== snapshotVersion) return;
          pendingSnapshotRef.current =
            analysis.hasMeaningfulInk && !analysis.isBlankOrWhite
              ? latestSnapshot
              : null;
        }

        if (pendingSnapshotVersionRef.current !== snapshotVersion) return;

        onDataChange({
          ...whiteboardDataRef.current,
          pdfFileId: data.pdfFileId,
          pdfPages: data.pdfPages,
          currentPdfPage: currentPage,
        });

      } else if (data.drawings && data.drawings.length > 0) {
        const latestSnapshot =
          Array.isArray(data.drawings)
            ? [...data.drawings].reverse().find((item) => typeof item === "string") || null
            : null;
        const persistedStrokes = extractStrokeHistory(data.drawings || []);

        pendingSnapshotRef.current = null;
        pendingJoinStrokesRef.current = persistedStrokes;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return;

        if (latestSnapshot && typeof latestSnapshot === "string") {
          const img = new Image();
          img.onload = () => {
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            drawSnapshotOnCanvas(ctx, canvas, img);
            renderPersistedJoinStrokes(ctx);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            onDataChange({
              ...whiteboardDataRef.current,
              canvasData: imageData,
              history: [imageData],
              historyStep: 0,
            });
          };
          img.src = latestSnapshot;
        } else {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          renderPersistedJoinStrokes(ctx);

          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          onDataChange({
            ...whiteboardDataRef.current,
            canvasData: imageData,
            history: [imageData],
            historyStep: 0,
          });
        }
      }
    });

    socket.on("draw", (data: { drawing: DrawingData & { userName?: string }; userId: string; userName?: string }) => {
      if (data.userId === user?.id) return;

      if (isPageLoading.current) {
        drawingBuffer.current.push({ drawing: data.drawing, userId: data.userId });
        return;
      }

      applyRemoteDrawing(data.drawing, data.userId);

      const userName = data.userName || data.drawing.userName || "Anonim";

      if (data.drawing.type === "start") {
        updateActionMessage(userName, data.drawing.tool);
      }
    });

    socket.on("drawing-start", (data: { userId: string; userName: string; tool: "pen" | "eraser" }) => {
      if (data.userId === user?.id) return;
      updateActionMessage(data.userName, data.tool);
    });

    socket.on("draw-batch", (data: { drawings: DrawingData[]; userId: string }) => {
      if (data.userId === user?.id) return;
      if (isPageLoading.current) {
        data.drawings.forEach((drawing) => {
          drawingBuffer.current.push({ drawing, userId: data.userId });
        });
        return;
      }
      data.drawings.forEach((drawing) => applyRemoteDrawing(drawing, data.userId));
    });

    socket.on("stroke-complete", (data: { stroke: DrawingData[] | CompactStrokeTransport; userId: string; userName?: string }) => {
      if (isClearingRef.current) return;
      if (data.userId === user?.id) return;
      const normalizedStroke = normalizeStrokePayload(data.stroke);
      if (!normalizedStroke || normalizedStroke.length < 2) return;

      if (isPageLoading.current) return;

      const renderedPointCount = remoteRenderedPointCounts.current.get(data.userId) || 0;
      const expectedSegmentCount = Math.max(0, normalizedStroke.length - 1);

      // Skip stroke-complete only when we are confident the full stroke
      // was already rendered incrementally. Otherwise, use stroke-complete
      // as authoritative data to heal missing/cut segments.
      if (renderedPointCount >= expectedSegmentCount && expectedSegmentCount > 0) {
        remoteDrawingPaths.current.set(data.userId, { x: 0, y: 0, isDrawing: false });
        remoteRenderedPointCounts.current.delete(data.userId);
        return;
      }

      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return;

      const previousCompositeOperation = ctx.globalCompositeOperation;

      const firstPoint = normalizedStroke[0];
      const firstResolvedPoint = toCanvasPoint(firstPoint);
      ctx.beginPath();
      ctx.moveTo(firstResolvedPoint.x, firstResolvedPoint.y);

      for (let i = 1; i < normalizedStroke.length; i++) {
        const point = normalizedStroke[i];
        const resolvedPoint = toCanvasPoint(point);
        ctx.lineTo(resolvedPoint.x, resolvedPoint.y);
      }

      if (firstPoint.tool === "pen") {
        ctx.globalCompositeOperation = "source-over";
        ctx.strokeStyle = firstPoint.color;
        ctx.lineWidth = firstPoint.size;
      } else {
        ctx.globalCompositeOperation = "destination-out";
        ctx.lineWidth = firstPoint.size;
      }
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.stroke();
      ctx.closePath();

      ctx.globalCompositeOperation = previousCompositeOperation;
      remoteRenderedPointCounts.current.delete(data.userId);
    });

    socket.on("clear-canvas", (data: { userId: string }) => {
      if (data.userId === user?.id) return;

      isClearingRef.current = true;

      const canvas = canvasRef.current;
      if (!canvas) {
        isClearingRef.current = false;
        return;
      }
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) {
        isClearingRef.current = false;
        return;
      }

      ctx.beginPath();

      resetTransientDrawingState();

      const previousCompositeOperation = ctx.globalCompositeOperation;
      ctx.globalCompositeOperation = "source-over";

      if (pdfBackgroundImageRef.current || whiteboardDataRef.current.pdfPages) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      } else {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      ctx.globalCompositeOperation = previousCompositeOperation;

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      onDataChange({
        ...whiteboardDataRef.current,
        canvasData: imageData,
        history: [imageData],
        historyStep: 0,
      });

      setTimeout(() => {
        isClearingRef.current = false;
      }, 2000);
    });

    socket.on("sync-state", (data: { canvasData: any[]; userId: string }) => {
      if (isClearingRef.current) return;
      if (data.userId === user?.id) return;

      if (isDrawingRef.current) return;

      if (Array.isArray(data.canvasData) && data.canvasData.length === 0) {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return;

        isClearingRef.current = true;
        ctx.beginPath();
        resetTransientDrawingState();

        if (pdfBackgroundImageRef.current || whiteboardDataRef.current.pdfPages) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        } else {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        onDataChange({
          ...whiteboardDataRef.current,
          canvasData: imageData,
          history: [imageData],
          historyStep: 0,
        });

        setTimeout(() => {
          isClearingRef.current = false;
        }, 2000);

        return;
      }

      if (data.canvasData && data.canvasData.length > 0) {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return;

        const latestSnapshot =
          Array.isArray(data.canvasData)
            ? [...data.canvasData].reverse().find((item) => typeof item === "string") || null
            : null;
        const persistedStrokes = extractStrokeHistory(data.canvasData || []);

        resetTransientDrawingState();

        if (latestSnapshot && typeof latestSnapshot === "string") {
          const img = new Image();
          img.onload = () => {
            if (isClearingRef.current) return;

            if (pdfBackgroundImageRef.current || whiteboardDataRef.current.pdfPages) {
              ctx.clearRect(0, 0, canvas.width, canvas.height);
            } else {
              ctx.fillStyle = "#ffffff";
              ctx.fillRect(0, 0, canvas.width, canvas.height);
            }
            drawSnapshotOnCanvas(ctx, canvas, img);
            pendingJoinStrokesRef.current = persistedStrokes;
            renderPersistedJoinStrokes(ctx);

            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            onDataChange({
              ...whiteboardDataRef.current,
              canvasData: imageData,
              history: [imageData],
              historyStep: 0,
            });
          };
          img.src = latestSnapshot;
          return;
        }

        if (persistedStrokes.length > 0) {
          if (pdfBackgroundImageRef.current || whiteboardDataRef.current.pdfPages) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
          } else {
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }

          pendingJoinStrokesRef.current = persistedStrokes;
          renderPersistedJoinStrokes(ctx);

          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          onDataChange({
            ...whiteboardDataRef.current,
            canvasData: imageData,
            history: [imageData],
            historyStep: 0,
          });
        }
      }
    });

    socket.on("permissions-updated", (data: { userId?: string; departmentId?: string; canDraw: boolean; canErase?: boolean; whiteboardId?: string; type?: string }) => {
      if (data.whiteboardId && data.whiteboardId !== whiteboardId) return;

      if (data.userId === user?.id) {
        onDataChange({
          ...whiteboardDataRef.current,
          myPermissions: {
            canDraw: data.canDraw,
            canErase: data.canErase,
          },
        });
      }
    });

    socket.on("participant-removed", (data: { userId?: string; departmentId?: string; ownerName?: string; reason?: string; whiteboardId?: string; message?: string }) => {
      if (data.whiteboardId && data.whiteboardId !== whiteboardId) return;

      const userDepartmentId = (user as any)?.department?.id || (user as any)?.departmentId;
      const isTargetUser = data.userId === user?.id || (data.departmentId && data.departmentId === userDepartmentId);

      if (isTargetUser) {
        socket.emit("leave-whiteboard", { whiteboardId, userId: user?.id });

        onCloseTab();
      }
    });

    socket.on("department-user-permissions-updated", (data: { participantId: string; userId: string; canDraw: boolean; canErase?: boolean; whiteboardId?: string }) => {
      if (data.whiteboardId && data.whiteboardId !== whiteboardId) return;

      if (data.userId === user?.id) {
        onDataChange({
          ...whiteboardDataRef.current,
          myPermissions: {
            canDraw: data.canDraw,
            canErase: data.canErase,
          },
        });
      }
    });

    socket.on("user-left", (data: { userId: string }) => {
      remoteDrawingPaths.current.delete(data.userId);
      remoteRenderedPointCounts.current.delete(data.userId);
    });

    socket.on("department-user-excluded", (data: { participantId: string; userId: string; ownerName?: string; whiteboardId?: string }) => {
      if (data.whiteboardId && data.whiteboardId !== whiteboardId) return;

      if (data.userId === user?.id) {
        socket.emit("leave-whiteboard", { whiteboardId, userId: user?.id });

        onCloseTab();
      }
    });

    socket.on("kicked-from-whiteboard", (data: { whiteboardId?: string; reason?: string; message?: string }) => {
      if (data.whiteboardId === whiteboardId) {
        onCloseTab();
      }
    });

    socket.on("drawing-interrupted", (data: { interruptedBy: string; interruptedByName: string; whiteboardId?: string }) => {
      if (data.whiteboardId === whiteboardId) {
        if (isDrawingRef.current) {
          const canvas = canvasRef.current;
          if (canvas) {
            const ctx = canvas.getContext("2d", { willReadFrequently: true });
            if (ctx) {
              ctx.closePath();
            }
          }

          if (socket && whiteboardId) {
            socket.emit("draw", {
              whiteboardId,
              userId: user?.id,
              userName: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
              drawing: {
                x: 0,
                y: 0,
                color: penColorRef.current,
                size: drawingToolRef.current === "pen" ? penSizeRef.current : eraserSizeRef.current,
                tool: drawingToolRef.current,
                type: "end",
              },
            });
          }
        }

        setIsDrawing(false);

        setBlockMessage(`${data.interruptedByName} çizmeye başladı`);
        setIsBlocked(true);

        if (blockTimeout.current) {
          clearTimeout(blockTimeout.current);
        }

        blockTimeout.current = setTimeout(() => {
          setIsBlocked(false);
          setBlockMessage("");
        }, 1000);
      }
    });

    socket.on("pdf-change", (data: { pdfFileId?: string; pdfPages?: PdfPage[]; currentPdfPage?: number; userId: string }) => {
      if (data.userId === user?.id) return;

      if (data.pdfPages && data.pdfPages.length > 0) {
        const currentPage = data.currentPdfPage || 1;
        const pageData = data.pdfPages[currentPage - 1];
        pendingSnapshotRef.current = null;

        onDataChange({
          ...whiteboardDataRef.current,
          pdfFileId: data.pdfFileId,
          pdfPages: data.pdfPages,
          currentPdfPage: currentPage,
        });

      }
    });

    socket.on("pdf-page-change", (data: { currentPdfPage: number; userId: string }) => {
      if (data.userId === user?.id) return;

      const pages = whiteboardDataRef.current.pdfPages;
      if (!pages || pages.length === 0) return;

      const newPage = data.currentPdfPage || 1;
      const pageData = pages[newPage - 1];

      if (pageData?.imageUrl) {
        saveCurrentPageDrawings();
        resetViewportToPdfCenter(false);

        drawingBuffer.current = [];

        onDataChange({
          ...whiteboardDataRef.current,
          currentPdfPage: newPage,
        });
      }
    });

    socket.on("pdf-close", (data: { userId: string }) => {
      if (data.userId === user?.id) return;

      setPdfBackgroundImage(null);
      setPdfRenderBounds(null);
      latestPdfLoadKeyRef.current = null;
      appliedPdfLoadKeyRef.current = null;
      baseZoomLevelRef.current = 1;
      basePanOffsetRef.current = { x: 0, y: 0 };

      pageDrawingsRef.current = {};

      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (ctx) {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          onDataChange({
            ...whiteboardDataRef.current,
            pdfPages: undefined,
            currentPdfPage: undefined,
            pdfFileId: undefined,
            canvasData: imageData,
            history: [imageData],
            historyStep: 0,
          });
        }
      }
    });

    // Listen for viewport sync from other participants (zoom + pan)
    socket.on("viewport-change", (data: { userId: string; zoomLevel: number; panOffsetX: number; panOffsetY: number; currentPdfPage?: number | null }) => {
      if (data.userId === user?.id) return;
      if (isPageLoading.current) return;

      const localPage = whiteboardDataRef.current.currentPdfPage || null;
      const remotePage = data.currentPdfPage || null;
      if (localPage !== remotePage) return;

      setZoomLevel(data.zoomLevel);
      zoomLevelRef.current = data.zoomLevel;
      const newPan = { x: data.panOffsetX, y: data.panOffsetY };
      setPanOffset(newPan);
      panOffsetRef.current = newPan;
    });

    return () => {
      socket.emit("leave-whiteboard", { whiteboardId, userId: user?.id });
      socket.off("whiteboard-state");
      socket.off("draw");
      socket.off("drawing-start");
      socket.off("draw-batch");
      socket.off("stroke-complete");
      socket.off("clear-canvas");
      socket.off("redo");
      socket.off("sync-state");
      socket.off("permissions-updated");
      socket.off("participant-removed");
      socket.off("department-user-permissions-updated");
      socket.off("department-user-excluded");
      socket.off("user-left");
      socket.off("kicked-from-whiteboard");
      socket.off("drawing-interrupted");
      socket.off("pdf-change");
      socket.off("pdf-page-change");
      socket.off("pdf-close");
      socket.off("viewport-change");

      if (outgoingDrawFrameRef.current !== null) {
        window.cancelAnimationFrame(outgoingDrawFrameRef.current);
        outgoingDrawFrameRef.current = null;
      }
      outgoingDrawQueueRef.current = [];

      if (actionTimeoutRef.current) {
        clearTimeout(actionTimeoutRef.current);
      }
      if (blockTimeout.current) {
        clearTimeout(blockTimeout.current);
      }
    };
  }, [socket, whiteboardId, user?.id]);

  const applyRemoteDrawing = (drawing: DrawingData, userId: string) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;
    const resolvedPoint = toCanvasPoint(drawing);

    const userPath = remoteDrawingPaths.current.get(userId) || { x: 0, y: 0, isDrawing: false };

    if (drawing.type === "start") {
      remoteDrawingPaths.current.set(userId, { x: resolvedPoint.x, y: resolvedPoint.y, isDrawing: true });
      remoteRenderedPointCounts.current.set(userId, 0);
    } else if (drawing.type === "draw") {
      if (!userPath.isDrawing) {
        remoteDrawingPaths.current.set(userId, { x: resolvedPoint.x, y: resolvedPoint.y, isDrawing: true });
        remoteRenderedPointCounts.current.set(userId, 0);
      } else {
        const previousCompositeOperation = ctx.globalCompositeOperation;

        ctx.beginPath();
        ctx.moveTo(userPath.x, userPath.y);
        ctx.lineTo(resolvedPoint.x, resolvedPoint.y);

        if (drawing.tool === "pen") {
          ctx.globalCompositeOperation = "source-over";
          ctx.strokeStyle = drawing.color;
          ctx.lineWidth = drawing.size;
        } else {
          ctx.globalCompositeOperation = "destination-out";
          ctx.lineWidth = drawing.size;
        }
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.stroke();

        ctx.globalCompositeOperation = previousCompositeOperation;
        const count = remoteRenderedPointCounts.current.get(userId) || 0;
        remoteRenderedPointCounts.current.set(userId, count + 1);

        remoteDrawingPaths.current.set(userId, { x: resolvedPoint.x, y: resolvedPoint.y, isDrawing: true });
      }
    } else if (drawing.type === "end") {
      remoteDrawingPaths.current.set(userId, { x: 0, y: 0, isDrawing: false });
      remoteRenderedPointCounts.current.delete(userId);
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    if (whiteboardData.canvasData) {
      ctx.putImageData(whiteboardData.canvasData, 0, 0);
    } else {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      if (whiteboardData.history.length === 0) {
        saveToHistory(false);
      }
    }
  }, [tabId]);

  // Canvas boyutlandırma ve HiDPI desteği
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const resizeCanvas = (restoreFromRef: boolean = false) => {
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const currentPage = whiteboardDataRef.current.currentPdfPage;
      const hasPdfBackground = Boolean(pdfBackgroundImageRef.current || whiteboardDataRef.current.pdfPages);

      // Yeni boyutlar
      const width = rect.width;
      const height = isFullscreen ? rect.height : 700;

      // Mevcut içeriği kaydet (resize öncesi)
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      let savedDataUrl: string | null = null;
      let oldWidth = canvas.width / dpr;
      let oldHeight = canvas.height / dpr;

      // Eğer ref'te önceden kaydedilmiş data varsa onu kullan (fullscreen geçişi için)
      if (hasPdfBackground && currentPage && ctx && canvas.width > 0 && canvas.height > 0) {
        const analysis = analyzeCanvasInk(canvas, true);
        if (analysis.hasMeaningfulInk) {
          pageDrawingsRef.current[currentPage] = canvas.toDataURL("image/png");
        } else {
          delete pageDrawingsRef.current[currentPage];
        }
      } else if (restoreFromRef && savedCanvasDataRef.current) {
        savedDataUrl = savedCanvasDataRef.current;
        savedCanvasDataRef.current = null; // Kullandıktan sonra temizle
      } else if (ctx && canvas.width > 0 && canvas.height > 0) {
        // Canvas içeriğini base64 olarak kaydet (boyut bağımsız)
        savedDataUrl = canvas.toDataURL("image/png");
      }

      // Canvas'ın fiziksel boyutlarını ayarla (yüksek çözünürlük için)
      canvas.width = width * dpr;
      canvas.height = height * dpr;

      // Canvas'ın görsel boyutlarını ayarla
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      syncPdfRenderBounds(canvas, pdfBackgroundImageRef.current);

      // Context'i ölçekle
      const newCtx = canvas.getContext("2d", { willReadFrequently: true });
      if (newCtx) {
        newCtx.scale(dpr, dpr);

        // Beyaz arka plan
        if (!pdfBackgroundImage) {
          newCtx.fillStyle = "#ffffff";
          newCtx.fillRect(0, 0, width, height);
        }

        if (hasPdfBackground && currentPage && pageDrawingsRef.current[currentPage]) {
          restorePageDrawings(currentPage);
          return;
        }

        // Kaydedilen içeriği geri yükle (orijinal boyutunda, stretch etme)
        if (savedDataUrl && oldWidth > 0 && oldHeight > 0) {
          const img = new Image();
          img.onload = () => {
            // Arka planı temizle
            if (!pdfBackgroundImage) {
              newCtx.fillStyle = "#ffffff";
              newCtx.fillRect(0, 0, width, height);
            }

            if (pdfBackgroundImage) {
              const previousBounds = getReferenceBounds(oldWidth, oldHeight, pdfBackgroundImage);
              const nextBounds = getReferenceBounds(width, height, pdfBackgroundImage);
              const sourceScaleX = img.naturalWidth / oldWidth;
              const sourceScaleY = img.naturalHeight / oldHeight;

              newCtx.drawImage(
                img,
                previousBounds.x * sourceScaleX,
                previousBounds.y * sourceScaleY,
                previousBounds.width * sourceScaleX,
                previousBounds.height * sourceScaleY,
                nextBounds.x,
                nextBounds.y,
                nextBounds.width,
                nextBounds.height
              );
            } else {
              newCtx.drawImage(img, 0, 0, width, height);
            }
          };
          img.src = savedDataUrl;
        }
      }
    };

    // İlk render'da veya fullscreen değiştiğinde ref'ten restore et
    resizeCanvas(true);

    return () => {
      // Cleanup'ta mevcut canvas içeriğini kaydet (fullscreen geçişi için)
      const canvasEl = canvasRef.current;
      if (canvasEl && canvasEl.width > 0 && canvasEl.height > 0) {
        const currentPage = whiteboardDataRef.current.currentPdfPage;
        if ((pdfBackgroundImageRef.current || whiteboardDataRef.current.pdfPages) && currentPage) {
          const analysis = analyzeCanvasInk(canvasEl, true);
          if (analysis.hasMeaningfulInk) {
            pageDrawingsRef.current[currentPage] = canvasEl.toDataURL("image/png");
          } else {
            delete pageDrawingsRef.current[currentPage];
          }
          savedCanvasDataRef.current = null;
        } else {
          savedCanvasDataRef.current = canvasEl.toDataURL("image/png");
        }
      }
    };
  }, [isFullscreen, pdfBackgroundImage]);

  useEffect(() => {
    if (pdfBackgroundImageRef.current) {
      fitPdfToViewport(pdfBackgroundImageRef.current);
    } else {
      baseZoomLevelRef.current = 1;
      basePanOffsetRef.current = { x: 0, y: 0 };
    }
  }, [isFullscreen]);

  const syncToServer = () => {
    if (!whiteboardId || !socket) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const base64Data = canvas.toDataURL("image/png");
    socket.emit("sync-state", {
      whiteboardId,
      userId: user?.id,
      canvasData: [base64Data],
    });
  };

  const saveWhiteboard = async () => {
    if (!whiteboardId || !socket) return;

    setIsSaving(true);
    try {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const base64Data = canvas.toDataURL("image/png");
      socket.emit("sync-state", {
        whiteboardId,
        userId: user?.id,
        canvasData: [base64Data],
      });

      setHasUnsavedChanges(false);
    } catch (error) {
    } finally {
      setIsSaving(false);
    }
  };

  const saveToHistory = (syncToDb = true) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const newHistory = whiteboardData.history.slice(0, whiteboardData.historyStep + 1);
    newHistory.push(imageData);
    const trimmedHistory =
      newHistory.length > MAX_HISTORY_ENTRIES
        ? newHistory.slice(newHistory.length - MAX_HISTORY_ENTRIES)
        : newHistory;

    onDataChange({
      ...whiteboardData,
      canvasData: imageData,
      history: trimmedHistory,
      historyStep: trimmedHistory.length - 1,
    });

    if (syncToDb && whiteboardId && socket) {
      if (isShared) {
        flushQueuedDrawings();
      } else {
        setHasUnsavedChanges(true);
      }
    }
  };

  const undo = () => {
    if (whiteboardData.historyStep <= 0) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    const newStep = whiteboardData.historyStep - 1;
    ctx.putImageData(whiteboardData.history[newStep], 0, 0);

    onDataChange({
      ...whiteboardData,
      canvasData: whiteboardData.history[newStep],
      historyStep: newStep,
    });

    if (whiteboardId && socket) {
      if (isShared) {
        socket.emit("undo", {
          whiteboardId,
          userId: user?.id,
        });
      } else {
        syncToServer();
      }
    }
  };

  const redo = () => {
    if (whiteboardData.historyStep >= whiteboardData.history.length - 1) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    const newStep = whiteboardData.historyStep + 1;
    ctx.putImageData(whiteboardData.history[newStep], 0, 0);

    onDataChange({
      ...whiteboardData,
      canvasData: whiteboardData.history[newStep],
      historyStep: newStep,
    });

    if (whiteboardId && socket) {
      if (isShared) {
        socket.emit("redo", {
          whiteboardId,
          userId: user?.id,
        });
      } else {
        syncToServer();
      }
    }
  };

  const clearCanvas = () => {
    if (!canErase) return;

    if (isClearingRef.current) return;
    if (isClearingUI) return;

    isClearingRef.current = true;
    setIsClearingUI(true);

    const canvas = canvasRef.current;
    if (!canvas) {
      isClearingRef.current = false;
      setIsClearingUI(false);
      return;
    }

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      isClearingRef.current = false;
      setIsClearingUI(false);
      return;
    }

    ctx.beginPath();
    currentStroke.current = [];

    const prevComposite = ctx.globalCompositeOperation;
    ctx.globalCompositeOperation = "source-over";

    if (pdfBackgroundImage) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    } else {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    ctx.globalCompositeOperation = prevComposite;

    remoteDrawingPaths.current.clear();
    resetTransientDrawingState();
    setIsDrawing(false);

    saveToHistory(false);

    if (socket && whiteboardId) {
      socket.emit("clear-canvas", { whiteboardId, userId: user?.id });
    }

    setTimeout(() => {
      isClearingRef.current = false;
    }, 2000);

    setTimeout(() => {
      setIsClearingUI(false);
    }, 1000);
  };

  const saveToFiles = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = canvas.width;
    exportCanvas.height = canvas.height;

    const exportCtx = exportCanvas.getContext("2d");
    if (!exportCtx) return;

    exportCtx.fillStyle = "#ffffff";
    exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);

    const pdfImg = pdfBackgroundImageRef.current;
    if (pdfImg) {
      const dpr = window.devicePixelRatio || 1;
      const cssWidth = exportCanvas.width / dpr;
      const cssHeight = exportCanvas.height / dpr;
      const bounds = getReferenceBounds(cssWidth, cssHeight, pdfImg);

      exportCtx.drawImage(
        pdfImg,
        bounds.x * dpr,
        bounds.y * dpr,
        bounds.width * dpr,
        bounds.height * dpr
      );
    }

    exportCtx.drawImage(canvas, 0, 0);

    exportCanvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `beyaz-tahta-${Date.now()}.png`;
        link.click();
        URL.revokeObjectURL(url);
      }
    });
  };

  const saveCurrentPageDrawings = () => {
    const currentPage = whiteboardDataRef.current.currentPdfPage;
    if (!currentPage || !whiteboardDataRef.current.pdfPages) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const analysis = analyzeCanvasInk(canvas, true);
    if (analysis.hasMeaningfulInk) {
      const base64Data = canvas.toDataURL("image/png");
      pageDrawingsRef.current[currentPage] = base64Data;
      return;
    }

    delete pageDrawingsRef.current[currentPage];
  };

  const restorePageDrawings = async (pageNumber: number): Promise<void> => {
    const savedDrawing = pageDrawingsRef.current[pageNumber];
    if (!savedDrawing) return;

    const analysis = await analyzeDataUrlInk(savedDrawing, true);
    if (!analysis.hasMeaningfulInk || analysis.isBlankOrWhite) {
      delete pageDrawingsRef.current[pageNumber];
      return;
    }

    await new Promise<void>((resolve) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        resolve();
        return;
      }

      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) {
        resolve();
        return;
      }

      const img = new Image();
      img.onload = () => {
        drawSnapshotOnCanvas(ctx, canvas, img);
        resolve();
      };
      img.onerror = () => {
        delete pageDrawingsRef.current[pageNumber];
        resolve();
      };
      img.src = savedDrawing;
    });
  };

  // ---- Zoom helpers ----
  const ZOOM_STEP = 0.25;
  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 3;

  const calculateZoomOriginFromContent = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // If PDF is loaded, use the PDF image's rendered center within the canvas
    const pdfImg = pdfBackgroundImageRef.current;
    if (pdfImg && pdfImg.naturalWidth > 0 && pdfImg.naturalHeight > 0) {
      const canvasW = canvas.width / (window.devicePixelRatio || 1);
      const canvasH = canvas.height / (window.devicePixelRatio || 1);
      // PDF is rendered with object-contain, calculate actual rendered size
      const scale = Math.min(canvasW / pdfImg.naturalWidth, canvasH / pdfImg.naturalHeight);
      const renderedW = pdfImg.naturalWidth * scale;
      const renderedH = pdfImg.naturalHeight * scale;
      const offsetX = (canvasW - renderedW) / 2;
      const offsetY = (canvasH - renderedH) / 2;
      const centerX = offsetX + renderedW / 2;
      const centerY = offsetY + renderedH / 2;
      setZoomOrigin({
        x: `${Math.round(centerX)}px`,
        y: `${Math.round(centerY)}px`,
      });
      return;
    }
    // For drawings: try to find bounding box of non-white pixels
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.width;
    const h = canvas.height;
    const data = ctx.getImageData(0, 0, w, h).data;
    let minX = w, minY = h, maxX = 0, maxY = 0;
    let found = false;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const idx = (y * w + x) * 4;
        const r = data[idx], g = data[idx + 1], b = data[idx + 2], a = data[idx + 3];
        // Consider pixel 'content' if not white/transparent
        if (a > 10 && !(r > 240 && g > 240 && b > 240)) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
          found = true;
        }
      }
    }
    if (found) {
      const centerX = ((minX + maxX) / 2) / dpr;
      const centerY = ((minY + maxY) / 2) / dpr;
      setZoomOrigin({ x: `${Math.round(centerX)}px`, y: `${Math.round(centerY)}px` });
    } else {
      setZoomOrigin({ x: '50%', y: '50%' });
    }
  };

  const emitViewportChange = (
    newZoom: number,
    newPan: { x: number; y: number },
    pageOverride?: number | null
  ) => {
    if (socket && whiteboardId) {
      socket.emit("viewport-change", {
        whiteboardId,
        userId: user?.id,
        zoomLevel: newZoom,
        panOffsetX: newPan.x,
        panOffsetY: newPan.y,
        currentPdfPage: pageOverride !== undefined
          ? pageOverride
          : (whiteboardDataRef.current.currentPdfPage || null),
      });
    }
  };

  const resetViewportToPdfCenter = (shouldEmit: boolean = false, pageOverride?: number | null) => {
    const targetZoom = 1;
    const targetPan = { x: 0, y: 0 };

    setZoomOrigin({ x: "50%", y: "50%" });
    setZoomLevel(targetZoom);
    zoomLevelRef.current = targetZoom;
    baseZoomLevelRef.current = targetZoom;
    setPanOffset(targetPan);
    panOffsetRef.current = targetPan;
    basePanOffsetRef.current = targetPan;

    if (shouldEmit) {
      emitViewportChange(targetZoom, targetPan, pageOverride);
    }
  };

  const handleZoomIn = () => {
    const next = Math.min(zoomLevelRef.current + ZOOM_STEP, ZOOM_MAX);
    setZoomLevel(next);
    zoomLevelRef.current = next;
    emitViewportChange(next, panOffsetRef.current);
  };

  const handleZoomOut = () => {
    const next = Math.max(zoomLevelRef.current - ZOOM_STEP, ZOOM_MIN);
    setZoomLevel(next);
    zoomLevelRef.current = next;
    emitViewportChange(next, panOffsetRef.current);
  };

  const handleResetZoom = () => {
    const targetZoom = pdfBackgroundImageRef.current ? baseZoomLevelRef.current : 1;
    const targetPan = pdfBackgroundImageRef.current ? basePanOffsetRef.current : { x: 0, y: 0 };
    setZoomLevel(targetZoom);
    zoomLevelRef.current = targetZoom;
    setZoomOrigin({ x: '50%', y: '50%' });
    setPanOffset(targetPan);
    panOffsetRef.current = targetPan;
    emitViewportChange(targetZoom, targetPan);
  };

  const fitPdfToViewport = (img: HTMLImageElement) => {
    if (!img.naturalWidth || !img.naturalHeight) return;
    resetViewportToPdfCenter(false);
  };

  // Pan handlers (used when drawingTool === 'hand' or while zoomed)
  const startPan = (clientX: number, clientY: number) => {
    isPanningRef.current = true;
    lastPanPointRef.current = { x: clientX, y: clientY };
  };

  const doPan = (clientX: number, clientY: number) => {
    if (!isPanningRef.current || !lastPanPointRef.current) return;
    const dx = clientX - lastPanPointRef.current.x;
    const dy = clientY - lastPanPointRef.current.y;
    lastPanPointRef.current = { x: clientX, y: clientY };
    const newPan = {
      x: panOffsetRef.current.x + dx,
      y: panOffsetRef.current.y + dy,
    };
    setPanOffset(newPan);
    panOffsetRef.current = newPan;
    emitViewportChange(zoomLevelRef.current, newPan);
  };

  const stopPan = () => {
    isPanningRef.current = false;
    lastPanPointRef.current = null;
  };
  // -----------------------

  const loadPdfPage = (imageUrl: string, newPageNumber?: number) => {
    if (!imageUrl) {
      alert("PDF sayfası yüklenemedi: URL bulunamadı");
      isPageLoading.current = false;
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      isPageLoading.current = false;
      return;
    }

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      isPageLoading.current = false;
      return;
    }

    const loadKey = `${newPageNumber || whiteboardDataRef.current.currentPdfPage || 1}:${imageUrl}`;
    latestPdfLoadKeyRef.current = loadKey;
    isPageLoading.current = true;

    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = async () => {
      if (latestPdfLoadKeyRef.current !== loadKey) {
        return;
      }

      setPdfBackgroundImage(img);
      syncPdfRenderBounds(canvas, img);
      fitPdfToViewport(img);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const pageToRestore = newPageNumber !== undefined ? newPageNumber : whiteboardDataRef.current.currentPdfPage || 1;
      await restorePageDrawings(pageToRestore);

      if (pendingSnapshotRef.current) {
        const pendingSnapshot = pendingSnapshotRef.current;
        const snapshotAnalysis = await analyzeDataUrlInk(pendingSnapshot, true);

        if (snapshotAnalysis.hasMeaningfulInk && !snapshotAnalysis.isBlankOrWhite) {
          const snapshotImg = new Image();
          snapshotImg.onload = () => {
            drawSnapshotOnCanvas(ctx, canvas, snapshotImg);
            renderPersistedJoinStrokes(ctx);

            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            onDataChange({
              ...whiteboardDataRef.current,
              canvasData: imageData,
              history: [imageData],
              historyStep: 0,
              ...(newPageNumber !== undefined && { currentPdfPage: newPageNumber }),
            });

            pendingSnapshotRef.current = null;
            appliedPdfLoadKeyRef.current = loadKey;
            isPageLoading.current = false;

            if (drawingBuffer.current.length > 0) {
              const bufferedDrawings = [...drawingBuffer.current];
              drawingBuffer.current = [];

              bufferedDrawings.forEach(({ drawing, userId }) => {
                applyRemoteDrawing(drawing, userId);
              });
            }
          };
          snapshotImg.onerror = () => {
            pendingSnapshotRef.current = null;
            isPageLoading.current = false;
          };
          snapshotImg.src = pendingSnapshot;
          return;
        }

        pendingSnapshotRef.current = null;
      }

      renderPersistedJoinStrokes(ctx);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      onDataChange({
        ...whiteboardDataRef.current,
        canvasData: imageData,
        history: [imageData],
        historyStep: 0,

        ...(newPageNumber !== undefined && { currentPdfPage: newPageNumber }),
      });

      appliedPdfLoadKeyRef.current = loadKey;
      isPageLoading.current = false;

      if (drawingBuffer.current.length > 0) {
        const bufferedDrawings = [...drawingBuffer.current];
        drawingBuffer.current = [];

        bufferedDrawings.forEach(({ drawing, userId }) => {
          applyRemoteDrawing(drawing, userId);
        });
      }
    };

    img.onerror = (error) => {
      if (latestPdfLoadKeyRef.current !== loadKey) {
        return;
      }
      alert("PDF sayfası yüklenemedi. Lütfen farklı bir PDF deneyin.");
      isPageLoading.current = false;
      drawingBuffer.current = [];
      setPdfRenderBounds(null);
    };

    img.src = imageUrl;
  };

  useEffect(() => {
    const pages = whiteboardData.currentPdfPage && whiteboardData.pdfPages
      ? whiteboardData.pdfPages
      : null;

    if (!pages || pages.length === 0) return;
    if (!canvasRef.current) return;

    const currentPage = whiteboardData.currentPdfPage || 1;
    const currentPageData = pages[currentPage - 1];
    if (!currentPageData?.imageUrl) return;

    const targetKey = `${currentPage}:${currentPageData.imageUrl}`;
    const currentImageSrc = pdfBackgroundImageRef.current?.src || "";
    if (currentImageSrc === currentPageData.imageUrl && appliedPdfLoadKeyRef.current === targetKey) return;
    if (latestPdfLoadKeyRef.current === targetKey || appliedPdfLoadKeyRef.current === targetKey) return;
    if (isPageLoading.current) return;

    loadPdfPage(currentPageData.imageUrl, currentPage);
  }, [
    tabId,
    whiteboardData.pdfPages,
    whiteboardData.currentPdfPage,
    isFullscreen,
  ]);

  const goToPdfPage = (targetPage: number) => {
    const pages = whiteboardDataRef.current.pdfPages;
    const currentPage = whiteboardDataRef.current.currentPdfPage || 1;

    if (!pages || pages.length === 0) return;

    if (isShared && !isOwner) {
      return;
    }

    if (!Number.isFinite(targetPage)) {
      setPdfPageInput(String(currentPage));
      return;
    }

    const newPage = Math.min(Math.max(Math.trunc(targetPage), 1), pages.length);
    setPdfPageInput(String(newPage));

    if (newPage !== currentPage) {
      saveCurrentPageDrawings();
      resetViewportToPdfCenter(false, newPage);

      if (socket && whiteboardId) {
        socket.emit("pdf-page-change", {
          whiteboardId,
          userId: user?.id,
          currentPdfPage: newPage,
        });
      }

      onDataChange({
        ...whiteboardDataRef.current,
        currentPdfPage: newPage,
      });

      if (whiteboardId) {
        ApiService.updateWhiteboard(whiteboardId, {
          currentPdfPage: newPage,
        }).catch(() => {
        });
      }
    }
  };

  const handlePdfPageChange = (direction: "prev" | "next") => {
    const pages = whiteboardDataRef.current.pdfPages;
    const currentPage = whiteboardDataRef.current.currentPdfPage || 1;

    if (!pages || pages.length === 0) return;

    if (direction === "prev") {
      goToPdfPage(currentPage - 1);
    } else {
      goToPdfPage(currentPage + 1);
    }
  };

  const handlePdfPageInputSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    goToPdfPage(Number(pdfPageInput));
  };

  const closePdf = async () => {
    setPdfBackgroundImage(null);
    setPdfRenderBounds(null);
    latestPdfLoadKeyRef.current = null;
    appliedPdfLoadKeyRef.current = null;
    baseZoomLevelRef.current = 1;
    basePanOffsetRef.current = { x: 0, y: 0 };

    pageDrawingsRef.current = {};

    onDataChange({
      ...whiteboardData,
      pdfPages: undefined,
      currentPdfPage: undefined,
      pdfFileId: undefined,
    });

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    onDataChange({
      ...whiteboardData,
      pdfPages: undefined,
      currentPdfPage: undefined,
      pdfFileId: undefined,
      canvasData: imageData,
      history: [imageData],
      historyStep: 0,
    });

    if (whiteboardId) {
      try {
        await ApiService.updateWhiteboard(whiteboardId, {
          pdfFileId: null,
          pdfPages: null,
          currentPdfPage: null,
        });
      } catch (error) {
      }
    }

    if (socket && whiteboardId && isOwner) {
      socket.emit("pdf-close", {
        whiteboardId,
        userId: user?.id,
      });
    }
  };

  const handlePdfSelect = async (file: any) => {
    if (!file.pages || file.pages.length === 0) {
      alert("Bu PDF dosyası henüz işlenmemiş veya sayfaları bulunamadı.");
      return;
    }

    pageDrawingsRef.current = {};

    const pdfPages = file.pages
      .map((page: any) => ({
        pageNumber: page.pageNumber,
        imageUrl: page.originalImageUrl || page.processedImageUrl || page.imageUrl || "",
        width: page.width || 0,
        height: page.height || 0,
      }))
      .filter((page: PdfPage) => Boolean(page.imageUrl));

    if (pdfPages.length === 0) {
      alert("Seçilen PDF'in sayfa görselleri bulunamadı.");
      return;
    }

    onDataChange({
      ...whiteboardDataRef.current,
      pdfPages: pdfPages,
      pdfFileId: file.id,
      currentPdfPage: 1,
    });

    if (whiteboardId) {
      try {
        await ApiService.updateWhiteboard(whiteboardId, {
          pdfFileId: file.id,
          pdfPages: pdfPages,
          currentPdfPage: 1,
        });
      } catch (error) {
      }
    }

    if (socket && whiteboardId) {
      socket.emit("pdf-change", {
        whiteboardId,
        userId: user?.id,
        pdfFileId: file.id,
        pdfPages: pdfPages,
        currentPdfPage: 1,
      });
    }
  };

  // Quadratic Bezier Curves ile çizgiyi pürüzsüzleştirme fonksiyonu
  const smoothStrokeWithQuadraticBezier = (
    ctx: CanvasRenderingContext2D,
    points: DrawingData[],
    canvas: HTMLCanvasElement
  ) => {
    if (points.length < 2) return;

    const firstPoint = points[0];
    const firstResolvedPoint = toCanvasPoint(firstPoint);

    // Canlı canvas snapshot'ını geri yükle.
    // Böylece uzaktan gelen çizimler local smoothing sırasında silinmez.
    if (preStrokeImageDataRef.current) {
      ctx.putImageData(preStrokeImageDataRef.current, 0, 0);
    }

    // Çizim ayarlarını uygula
    ctx.beginPath();
    if (firstPoint.tool === "pen") {
      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = firstPoint.color;
      ctx.lineWidth = firstPoint.size;
    } else {
      ctx.globalCompositeOperation = "destination-out";
      ctx.lineWidth = firstPoint.size;
    }
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    // İlk noktadan başla
    ctx.moveTo(firstResolvedPoint.x, firstResolvedPoint.y);

    if (points.length === 2) {
      // Sadece 2 nokta varsa düz çizgi çiz
      const secondResolvedPoint = toCanvasPoint(points[1]);
      ctx.lineTo(secondResolvedPoint.x, secondResolvedPoint.y);
    } else {
      // Quadratic Bezier ile pürüzsüz eğri çiz
      // Her üç ardışık nokta için: orta noktalar kontrol noktası, 
      // bir sonraki orta nokta bitiş noktası olur
      for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[i];
        const p1 = points[i + 1];
        const resolvedP0 = toCanvasPoint(p0);
        const resolvedP1 = toCanvasPoint(p1);

        if (i === 0) {
          // İlk segment için başlangıç noktasından başla
          ctx.moveTo(resolvedP0.x, resolvedP0.y);
        }

        if (i < points.length - 2) {
          // Orta noktaları kontrol noktası olarak kullan
          const p2 = points[i + 2];
          const resolvedP2 = toCanvasPoint(p2);

          // p1 kontrol noktası, p1 ve p2'nin ortası bitiş noktası
          const midX = (resolvedP1.x + resolvedP2.x) / 2;
          const midY = (resolvedP1.y + resolvedP2.y) / 2;

          ctx.quadraticCurveTo(resolvedP1.x, resolvedP1.y, midX, midY);
        } else {
          // Son segment için doğrudan son noktaya çiz
          ctx.lineTo(resolvedP1.x, resolvedP1.y);
        }
      }
    }

    ctx.stroke();
    ctx.closePath();
  };

  const getCanvasCoordinates = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    // When CSS scale() is applied, getBoundingClientRect returns scaled positions.
    // We must divide by the zoom level to get the true canvas coordinate.
    const zoom = zoomLevelRef.current;
    return {
      x: (clientX - rect.left) / zoom,
      y: (clientY - rect.top) / zoom,
    };
  };

  const renderDrawingSegment = (
    ctx: CanvasRenderingContext2D,
    from: DrawingData,
    to: DrawingData
  ) => {
    const fromPoint = toCanvasPoint(from);
    const toPoint = toCanvasPoint(to);

    ctx.beginPath();
    ctx.moveTo(fromPoint.x, fromPoint.y);
    ctx.lineTo(toPoint.x, toPoint.y);

    if (to.tool === "pen") {
      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = to.color;
      ctx.lineWidth = to.size;
    } else {
      ctx.globalCompositeOperation = "destination-out";
      ctx.lineWidth = to.size;
    }

    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
  };

  const shouldCaptureDrawingPoint = (
    previousPoint: DrawingData | undefined,
    nextPoint: DrawingData
  ) => {
    if (!previousPoint) return true;

    const previousResolvedPoint = toCanvasPoint(previousPoint);
    const nextResolvedPoint = toCanvasPoint(nextPoint);
    const dx = nextResolvedPoint.x - previousResolvedPoint.x;
    const dy = nextResolvedPoint.y - previousResolvedPoint.y;

    return Math.hypot(dx, dy) >= MIN_POINT_DISTANCE_PX;
  };

  const canPerformDrawing = () => {
    if (isBlocked) return false;
    if (drawingToolRef.current === 'hand') return false;
    if (drawingTool === 'eraser') return canErase;
    return canDraw;
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setActiveMenu(null);
    // Hand tool: start pan
    if (drawingToolRef.current === 'hand') {
      startPan(e.clientX, e.clientY);
      return;
    }

    if (isBlocked || !canPerformDrawing()) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x, y } = getCanvasCoordinates(e.clientX, e.clientY);

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (ctx) {
      setIsDrawing(true);
      preStrokeImageDataRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);

      const startPoint = createDrawingPoint(x, y, drawingTool as "pen" | "eraser", "start");
      const resolvedStartPoint = toCanvasPoint(startPoint);
      ctx.beginPath();
      ctx.moveTo(resolvedStartPoint.x, resolvedStartPoint.y);
      currentStroke.current = [startPoint];

      if (socket && whiteboardId) {
        socket.emit("drawing-start", {
          whiteboardId,
          userId: user?.id,
          userName: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
          tool: drawingTool,
        });
        emitImmediateDrawing(startPoint);
      }
    }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // Hand tool: pan
    if (drawingToolRef.current === 'hand') {
      doPan(e.clientX, e.clientY);
      return;
    }

    if (!isDrawing || !canPerformDrawing()) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x, y } = getCanvasCoordinates(e.clientX, e.clientY);

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (ctx) {
      const drawingPoint = createDrawingPoint(x, y, drawingTool as "pen" | "eraser", "draw");
      const previousPoint = currentStroke.current[currentStroke.current.length - 1];
      if (!shouldCaptureDrawingPoint(previousPoint, drawingPoint)) {
        return;
      }

      if (previousPoint) {
        renderDrawingSegment(ctx, previousPoint, drawingPoint);
      }

      currentStroke.current.push(drawingPoint);

      if (socket && whiteboardId) {
        queueDrawingForBroadcast(drawingPoint);
      }
    }
  };

  const stopDrawing = () => {
    // Stop panning
    if (drawingToolRef.current === 'hand') {
      stopPan();
      return;
    }

    if (isDrawing) {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d", { willReadFrequently: true });

      // Quadratic Bezier ile pürüzsüzleştirme uygula
      if (ctx && canvas && currentStroke.current.length > 2) {
        smoothStrokeWithQuadraticBezier(ctx, currentStroke.current, canvas);
      }

      setIsDrawing(false);
      saveToHistory();

      if (socket && whiteboardId) {
        flushQueuedDrawings();
        const transportStroke = buildCompactStrokeTransport(currentStroke.current);

        if (transportStroke && transportStroke.p.length > 1) {
          socket.emit("stroke-complete", {
            whiteboardId,
            userId: user?.id,
            userName: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
            stroke: transportStroke,
          });
        }
        currentStroke.current = [];

        emitImmediateDrawing({
          x: 0,
          y: 0,
          color: penColor,
          size: drawingTool === "pen" ? penSize : eraserSize,
          tool: drawingTool as "pen" | "eraser",
          type: "end",
        });

        socket.emit("drawing-end", {
          whiteboardId,
          userId: user?.id,
        });
      }

      preStrokeImageDataRef.current = null;
    }
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    setActiveMenu(null);
    const touch = e.touches[0];

    if (drawingToolRef.current === 'hand') {
      startPan(touch.clientX, touch.clientY);
      return;
    }

    if (isBlocked || !canPerformDrawing()) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x, y } = getCanvasCoordinates(touch.clientX, touch.clientY);

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (ctx) {
      setIsDrawing(true);
      preStrokeImageDataRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);

      const startPoint = createDrawingPoint(x, y, drawingTool as "pen" | "eraser", "start");
      const resolvedStartPoint = toCanvasPoint(startPoint);
      ctx.beginPath();
      ctx.moveTo(resolvedStartPoint.x, resolvedStartPoint.y);
      currentStroke.current = [startPoint];

      if (socket && whiteboardId) {
        socket.emit("drawing-start", {
          whiteboardId,
          userId: user?.id,
          userName: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
          tool: drawingTool,
        });
        emitImmediateDrawing(startPoint);
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const touch = e.touches[0];

    if (drawingToolRef.current === 'hand') {
      doPan(touch.clientX, touch.clientY);
      return;
    }

    if (!isDrawing || !canPerformDrawing()) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x, y } = getCanvasCoordinates(touch.clientX, touch.clientY);

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (ctx) {
      const drawingPoint = createDrawingPoint(x, y, drawingTool as "pen" | "eraser", "draw");
      const previousPoint = currentStroke.current[currentStroke.current.length - 1];
      if (!shouldCaptureDrawingPoint(previousPoint, drawingPoint)) {
        return;
      }

      if (previousPoint) {
        renderDrawingSegment(ctx, previousPoint, drawingPoint);
      }

      currentStroke.current.push(drawingPoint);

      if (socket && whiteboardId) {
        queueDrawingForBroadcast(drawingPoint);
      }
    }
  };

  const handleTouchEnd = () => {
    if (drawingToolRef.current === 'hand') {
      stopPan();
      return;
    }

    if (isDrawing) {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d", { willReadFrequently: true });

      // Quadratic Bezier ile pürüzsüzleştirme uygula
      if (ctx && canvas && currentStroke.current.length > 2) {
        smoothStrokeWithQuadraticBezier(ctx, currentStroke.current, canvas);
      }

      setIsDrawing(false);
      saveToHistory();

      if (socket && whiteboardId) {
        flushQueuedDrawings();
        const transportStroke = buildCompactStrokeTransport(currentStroke.current);

        if (transportStroke && transportStroke.p.length > 1) {
          socket.emit("stroke-complete", {
            whiteboardId,
            userId: user?.id,
            userName: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
            stroke: transportStroke,
          });
        }
        currentStroke.current = [];

        emitImmediateDrawing({
          x: 0,
          y: 0,
          color: penColor,
          size: drawingTool === "pen" ? penSize : eraserSize,
          tool: drawingTool as "pen" | "eraser",
          type: "end",
        });

        socket.emit("drawing-end", {
          whiteboardId,
          userId: user?.id,
        });
      }

      preStrokeImageDataRef.current = null;
    }
  };

  const getSharedLabel = () => {
    if (!isShared || isOwner) return null;
    const ownerName = sharedBy ? `${sharedBy.firstName || ""} ${sharedBy.lastName || ""}`.trim() : "";
    return ownerName ? `Paylaşılan Tahta - ${ownerName}` : "Paylaşılan Tahta";
  };

  return (
    <div
      className={`h-full overflow-auto relative ${isFullscreen ? 'p-4 bg-white' : 'p-6 bg-white'}`}
    >
      <div className={isFullscreen ? 'h-full flex flex-col' : 'max-w-7xl mx-auto'}>
        <div className="mb-4 flex flex-col gap-2">
          <div className="flex flex-col gap-2 sm:items-end">
            {isSharingStopped && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm">
                <AlertCircle size={16} className="text-red-600 flex-shrink-0" />
                <span className="text-red-800 font-medium">
                  Paylaşımı durduruldu{stoppedByOwnerName ? ` - ${stoppedByOwnerName}` : ''}
                </span>
              </div>
            )}
            {!isSharingStopped && getSharedLabel() && (
              <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-md px-3 py-2 text-sm">
                <Share2 size={16} className="text-purple-600 flex-shrink-0" />
                <span className="text-purple-800 font-medium">{getSharedLabel()}</span>
              </div>
            )}
            {!canDraw && !isSharingStopped && (
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm">
                <AlertCircle size={16} className="text-amber-600 flex-shrink-0" />
                <span className="text-amber-800">Sadece görüntüleme yetkiniz var.</span>
              </div>
            )}
          </div>
        </div>

        {/* New Layout: Sidebar + Canvas */}
        <div className={`flex gap-4 ${isFullscreen ? 'flex-1 min-h-0' : ''}`} style={{ height: isFullscreen ? undefined : '700px' }}>
          {/* LEFT SIDEBAR PANEL */}
          <div
            className="w-[72px] flex-shrink-0 flex flex-col items-center gap-3 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.12)] rounded-3xl py-4 border border-gray-100 relative z-40 overflow-visible"
          >
            <button
              onClick={() => {
                setDrawingTool("pen");
                if (drawingTool === "pen") setActiveMenu(activeMenu === "pen" ? null : "pen");
                else setActiveMenu("pen");
              }}
              disabled={!canDraw}
              className={`w-[48px] h-[48px] flex items-center justify-center rounded-2xl transition-all duration-300 ${drawingTool === "pen" ? "bg-blue-600 text-white shadow-lg shadow-blue-200" : "bg-transparent text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              title={canDraw ? "Kalem" : "Çizim yetkiniz yok"}
            >
              <Pen size={22} className={drawingTool === "pen" ? "scale-110" : ""} />
            </button>

            <button
              onClick={() => {
                setDrawingTool("eraser");
                if (drawingTool === "eraser") setActiveMenu(activeMenu === "eraser" ? null : "eraser");
                else setActiveMenu("eraser");
              }}
              disabled={!canErase}
              className={`w-[48px] h-[48px] flex items-center justify-center rounded-2xl transition-all duration-300 ${drawingTool === "eraser" ? "bg-blue-600 text-white shadow-lg shadow-blue-200" : "bg-transparent text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              title={canErase ? "Silgi" : "Silme yetkiniz yok"}
            >
              <Eraser size={22} className={drawingTool === "eraser" ? "scale-110" : ""} />
            </button>

            <button
              onClick={() => {
                setDrawingTool("hand");
                setActiveMenu(null);
              }}
              disabled={!canDraw && !canErase}
              className={`w-[48px] h-[48px] flex items-center justify-center rounded-2xl transition-all duration-300 ${!canDraw && !canErase ? "opacity-50 cursor-not-allowed text-gray-400" : drawingTool === "hand" ? "bg-amber-500 text-white shadow-lg shadow-amber-200" : "bg-transparent text-gray-400 hover:bg-gray-100 hover:text-amber-600"
                }`}
              title={!canDraw && !canErase ? "Yetkiniz yok" : "Boşta - Sürükleyerek kaydır"}
            >
              <Hand size={22} className={drawingTool === "hand" ? "scale-110" : ""} />
            </button>

            {canDraw && (
              <button
                onClick={() => {
                  setDrawingTool("pen");
                  setActiveMenu(activeMenu === "color" ? null : "color");
                }}
                className={`w-[48px] h-[48px] flex items-center justify-center rounded-2xl transition-all duration-300 ${activeMenu === "color" ? "bg-gray-100 shadow-inner" : "bg-transparent hover:bg-gray-100"
                  }`}
                title="Renk Seçici"
              >
                <div
                  className={`w-6 h-6 rounded-full border-[3px] transition-transform duration-300 ${activeMenu === "color" ? "border-blue-400 scale-110 shadow-md" : "border-gray-200"}`}
                  style={{ backgroundColor: penColor }}
                />
              </button>
            )}

            <div className="w-8 h-px bg-gray-200 my-1"></div>

            <button
              onClick={undo}
              disabled={whiteboardData.historyStep <= 0 || !canDraw}
              className={`w-[48px] h-[48px] flex items-center justify-center rounded-2xl transition-all ${!canDraw || whiteboardData.historyStep <= 0 ? "opacity-30 cursor-not-allowed text-gray-300" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                }`}
              title="Geri Al"
            >
              <Undo size={20} />
            </button>

            <button
              onClick={redo}
              disabled={whiteboardData.historyStep >= whiteboardData.history.length - 1 || !canDraw}
              className={`w-[48px] h-[48px] flex items-center justify-center rounded-2xl transition-all ${!canDraw || whiteboardData.historyStep >= whiteboardData.history.length - 1 ? "opacity-30 cursor-not-allowed text-gray-300" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                }`}
              title="İleri Al"
            >
              <Redo size={20} />
            </button>

            <button
              onClick={clearCanvas}
              disabled={!canErase || isClearingUI}
              className={`w-[48px] h-[48px] flex items-center justify-center rounded-2xl transition-all ${!canErase ? "opacity-30 cursor-not-allowed text-gray-300" : "text-red-400 hover:bg-red-50 hover:text-red-600"
                } ${isClearingUI ? 'cursor-wait animate-pulse' : ''}`}
              title="Tümünü Temizle"
            >
              <Trash2 size={20} />
            </button>

            {(canDraw || canErase) && (
              <>
                <div className="w-8 h-px bg-gray-200 my-1 flex-shrink-0"></div>

                <button
                  onClick={(e) => { e.stopPropagation(); handleZoomIn(); }}
                  disabled={zoomLevel >= 3}
                  className={`w-[48px] h-[48px] flex flex-shrink-0 items-center justify-center rounded-2xl transition-all ${zoomLevel >= 3 ? "opacity-30 cursor-not-allowed text-gray-300" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"}`}
                  title="Yakınlaştır"
                >
                  <ZoomIn size={20} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleResetZoom(); }}
                  className="text-[12px] font-bold text-gray-600 hover:text-blue-600 transition-colors w-full text-center flex-shrink-0"
                  title="Sıfırla"
                >
                  {Math.round((zoomLevel / (pdfBackgroundImageRef.current ? baseZoomLevelRef.current || 1 : 1)) * 100)}%
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleZoomOut(); }}
                  disabled={zoomLevel <= 0.5}
                  className={`w-[48px] h-[48px] flex flex-shrink-0 items-center justify-center rounded-2xl transition-all ${zoomLevel <= 0.5 ? "opacity-30 cursor-not-allowed text-gray-300" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"}`}
                  title="Uzaklaştır"
                >
                  <ZoomOut size={20} />
                </button>
              </>
            )}

            {whiteboardData.pdfPages && whiteboardData.pdfPages.length > 0 && (
              <>
                <div className="w-8 h-px bg-gray-200 my-1 flex-shrink-0"></div>

                <button
                  onClick={(e) => { e.stopPropagation(); handlePdfPageChange("next"); }}
                  disabled={isShared && !isOwner ? true : (whiteboardData.currentPdfPage || 1) >= whiteboardData.pdfPages.length}
                  className={`w-[48px] h-[48px] flex flex-shrink-0 items-center justify-center rounded-2xl transition-all ${isShared && !isOwner
                    ? "opacity-30 cursor-not-allowed text-gray-300"
                    : (whiteboardData.currentPdfPage || 1) >= whiteboardData.pdfPages.length
                      ? "opacity-30 cursor-not-allowed text-gray-300"
                      : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"}`}
                  title={isShared && !isOwner ? "PDF sayfasını sadece paylaşan kişi değiştirebilir" : "Sonraki Sayfa"}
                >
                  <ChevronRight size={20} />
                </button>
                <form
                  onSubmit={handlePdfPageInputSubmit}
                  className="w-[48px] flex flex-col items-center gap-0.5 flex-shrink-0"
                  title={isShared && !isOwner ? "PDF sayfasını sadece paylaşan kişi değiştirebilir" : "Sayfa numarası gir"}
                >
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={pdfPageInput}
                    disabled={isShared && !isOwner}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setPdfPageInput(e.target.value.replace(/\D/g, ""))}
                    onBlur={() => goToPdfPage(Number(pdfPageInput))}
                    className={`h-6 w-[42px] rounded-lg border px-1 text-center text-[11px] font-bold outline-none transition-colors ${isShared && !isOwner
                      ? "cursor-not-allowed border-gray-100 bg-gray-50 text-gray-300"
                      : "border-gray-200 bg-white text-gray-600 focus:border-blue-400 focus:ring-2 focus:ring-blue-100"}`}
                    aria-label="PDF sayfa numarası"
                  />
                  <div className="text-[10px] font-bold text-gray-400 leading-none">
                    / {whiteboardData.pdfPages.length}
                  </div>
                </form>
                <button
                  onClick={(e) => { e.stopPropagation(); handlePdfPageChange("prev"); }}
                  disabled={isShared && !isOwner ? true : (whiteboardData.currentPdfPage || 1) <= 1}
                  className={`w-[48px] h-[48px] flex flex-shrink-0 items-center justify-center rounded-2xl transition-all ${isShared && !isOwner
                    ? "opacity-30 cursor-not-allowed text-gray-300"
                    : (whiteboardData.currentPdfPage || 1) <= 1
                      ? "opacity-30 cursor-not-allowed text-gray-300"
                      : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"}`}
                  title={isShared && !isOwner ? "PDF sayfasını sadece paylaşan kişi değiştirebilir" : "Önceki Sayfa"}
                >
                  <ChevronLeft size={20} />
                </button>
              </>
            )}

            {/* FLYOUT PANELS - Thickness Adjustments */}
            {canDraw && (
              <div
                className={`absolute left-[84px] top-4 bg-white/95 backdrop-blur shadow-[0_8px_30px_rgb(0,0,0,0.12)] rounded-2xl p-4 flex flex-col gap-3 transition-all duration-300 origin-left border border-gray-100 ${activeMenu === "pen" ? 'scale-100 opacity-100 visible translate-x-0' : 'scale-95 opacity-0 invisible -translate-x-4 pointer-events-none'
                  }`}
              >
                <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">Kalem Kalınlığı</div>
                <div className="flex items-center gap-4 px-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-400"></div>
                  <input
                    type="range"
                    min="1"
                    max="20"
                    value={penSize}
                    onChange={(e) => setPenSize(Number(e.target.value))}
                    className="w-32 h-1.5 rounded-lg appearance-none cursor-pointer bg-gray-200 accent-blue-600 outline-none"
                    style={{ WebkitAppearance: 'none' }}
                  />
                  <div className="w-4 h-4 rounded-full bg-gray-400"></div>
                  <span className="text-gray-700 text-sm font-bold w-6 text-right bg-blue-50 text-blue-700 py-0.5 rounded-md">{penSize}</span>
                </div>
              </div>
            )}

            {canErase && (
              <div
                className={`absolute left-[84px] top-20 bg-white/95 backdrop-blur shadow-[0_8px_30px_rgb(0,0,0,0.12)] rounded-2xl p-4 flex flex-col gap-3 transition-all duration-300 origin-left border border-gray-100 ${activeMenu === "eraser" ? 'scale-100 opacity-100 visible translate-x-0' : 'scale-95 opacity-0 invisible -translate-x-4 pointer-events-none'
                  }`}
              >
                <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">Silgi Boyutu</div>
                <div className="flex items-center gap-4 px-1">
                  <div className="w-2 h-2 rounded-full border-[1.5px] border-gray-400"></div>
                  <input
                    type="range"
                    min="5"
                    max="50"
                    value={eraserSize}
                    onChange={(e) => setEraserSize(Number(e.target.value))}
                    className="w-32 h-1.5 rounded-lg appearance-none cursor-pointer bg-gray-200 accent-blue-600 outline-none"
                    style={{ WebkitAppearance: 'none' }}
                  />
                  <div className="w-6 h-6 rounded-full border-2 border-gray-400"></div>
                  <span className="text-gray-700 text-sm font-bold w-6 text-right bg-blue-50 text-blue-700 py-0.5 rounded-md">{eraserSize}</span>
                </div>
              </div>
            )}

            {canDraw && (
              <div
                className={`absolute left-[84px] top-36 bg-white/95 backdrop-blur shadow-[0_8px_30px_rgb(0,0,0,0.12)] rounded-2xl p-4 flex flex-col gap-3 transition-all duration-300 origin-left border border-gray-100 ${activeMenu === "color" ? 'scale-100 opacity-100 visible translate-x-0' : 'scale-95 opacity-0 invisible -translate-x-4 pointer-events-none'
                  }`}
              >
                <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">Renk Paleti</div>
                <div className="flex gap-2.5 items-center justify-center px-1">
                  {colorOptions.map((color) => (
                    <button
                      key={color.value}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPenColor(color.value);
                      }}
                      className={`w-8 h-8 rounded-full border-2 transition-all duration-300 hover:scale-110 ${penColor === color.value ? "border-white shadow-[0_0_0_4px_rgba(0,0,0,0.15)] scale-110" : "border-transparent"
                        }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Canvas outer container */}
          <div
            className={`rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-gray-200 bg-gray-50 relative overflow-hidden flex-1 min-h-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px]`}
          >
            {/* Action Message Top Left */}
            {lastActionMessage && (
              <div className="absolute top-6 left-6 z-30 flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur text-blue-700 border border-blue-100 shadow-sm rounded-xl text-sm font-medium animate-pulse">
                <Edit3 size={16} />
                <span>{lastActionMessage}</span>
              </div>
            )}

            {/* Colors Bottom Floating Removed as moved to Left Sidebar */}

            {/* Removed old Zoom & PDF Controls */}

            {/* Inner zoomable + pannable wrapper */}
            <div
              ref={canvasWrapperRef}
              style={{
                transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
                transformOrigin: `${zoomOrigin.x} ${zoomOrigin.y}`,
                transition: isPanningRef.current ? 'none' : 'transform 0.18s ease',
                width: '100%',
                height: isFullscreen ? '100%' : '700px',
              }}
            >
              {/* Canvas container, actual drawing surface */}
              <div
                ref={containerRef}
                className="w-full h-full bg-white relative"
              >
                {pdfBackgroundImage && (
                  <img
                    src={pdfBackgroundImage.src}
                    alt="PDF Arkaplan"
                    className="absolute pointer-events-none"
                    style={{
                      zIndex: 0,
                      backgroundColor: '#ffffff',
                      left: `${pdfRenderBounds?.x || 0}px`,
                      top: `${pdfRenderBounds?.y || 0}px`,
                      width: `${pdfRenderBounds?.width || 0}px`,
                      height: `${pdfRenderBounds?.height || 0}px`,
                    }}
                  />
                )}
                <canvas
                  ref={setCanvasRef}
                  className={`w-full relative ${isFullscreen ? 'h-full' : ''} ${drawingTool === "hand"
                    ? isPanningRef.current ? "cursor-grabbing" : "cursor-grab"
                    : drawingTool === "eraser" && canErase
                      ? "cursor-none"
                      : canPerformDrawing()
                        ? "cursor-crosshair"
                        : "cursor-not-allowed"
                    }`}
                  style={{ backgroundColor: pdfBackgroundImage ? "transparent" : "#ffffff", touchAction: "none", display: "block", zIndex: 1, height: isFullscreen ? '100%' : '700px' }}
                  onMouseDown={startDrawing}
                  onMouseMove={(e) => {
                    draw(e);

                    if (drawingTool === "eraser" && canErase && eraserCursorRef.current) {
                      const canvas = canvasRef.current;
                      if (canvas) {
                        const coords = getCanvasCoordinates(e.clientX, e.clientY);

                        eraserCursorRef.current.style.left = `${coords.x}px`;
                        eraserCursorRef.current.style.top = `${coords.y}px`;
                        eraserCursorRef.current.style.display = 'block';
                      }
                    }
                  }}
                  onMouseUp={stopDrawing}
                  onMouseLeave={() => {
                    stopDrawing();

                    if (eraserCursorRef.current) {
                      eraserCursorRef.current.style.display = 'none';
                    }
                    containerRectCache.current = null;
                  }}
                  onMouseEnter={(e) => {
                    containerRectCache.current = null;
                    if (drawingTool === "eraser" && canErase && eraserCursorRef.current) {
                      const canvas = canvasRef.current;
                      if (canvas) {
                        const coords = getCanvasCoordinates(e.clientX, e.clientY);

                        eraserCursorRef.current.style.left = `${coords.x}px`;
                        eraserCursorRef.current.style.top = `${coords.y}px`;
                        eraserCursorRef.current.style.display = 'block';
                      }
                    }
                  }}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onTouchCancel={handleTouchEnd}
                />

                {drawingTool === "eraser" && canErase && (
                  <div
                    ref={eraserCursorRef}
                    className="pointer-events-none absolute border-2 border-gray-600 rounded-full bg-white/30"
                    style={{
                      width: `${eraserSize}px`,
                      height: `${eraserSize}px`,
                      transform: "translate(-50%, -50%)",
                      display: 'none',
                      zIndex: 50,
                    }}
                  />
                )}

                {isBlocked && blockMessage && (
                  <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20 animate-pulse">
                    <div className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg">
                      <AlertCircle size={20} />
                      <span className="font-medium">{blockMessage}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="w-[72px] flex-shrink-0 flex flex-col items-center gap-3 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.12)] rounded-3xl py-4 border border-gray-100 relative z-40">
            {isOwner && !isShared && !whiteboardData.pdfPages && (
              <button
                onClick={saveWhiteboard}
                disabled={isSaving || !hasUnsavedChanges}
                className={`${rightSidebarButtonBase} ${hasUnsavedChanges
                  ? "bg-green-600 text-white shadow-lg shadow-green-200 hover:bg-green-700"
                  : "bg-transparent text-gray-300"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                title={isSaving ? "Kaydediliyor..." : hasUnsavedChanges ? "Kaydet" : "Kaydedilmemiş değişiklik yok"}
              >
                {isSaving ? <Loader2 size={22} className="animate-spin" /> : <Save size={22} />}
              </button>
            )}

            {isOwner && !isShared && hasUnsavedChanges && !whiteboardData.pdfPages && (
              <div
                className="flex h-[48px] w-[48px] items-center justify-center rounded-2xl bg-amber-50 text-amber-600 border border-amber-100"
                title="Kaydedilmemiş değişiklikler"
              >
                <div className="w-3 h-3 bg-amber-500 rounded-full animate-pulse" />
              </div>
            )}

            <button
              onClick={saveToFiles}
              className={`${rightSidebarButtonBase} text-orange-500 hover:bg-orange-50 hover:text-orange-600`}
              title="Dışa Aktar"
            >
              <Download size={22} />
            </button>

            {isOwner && (
              <>
                <button
                  onClick={() => setIsPdfSelectModalOpen(true)}
                  className={`${rightSidebarButtonBase} text-teal-600 hover:bg-teal-50 hover:text-teal-700`}
                  title="PDF Seç"
                >
                  <FolderOpen size={22} />
                </button>
                {whiteboardData.pdfPages && whiteboardData.pdfPages.length > 0 && (
                  <button
                    onClick={closePdf}
                    className={`${rightSidebarButtonBase} text-gray-500 hover:bg-gray-100 hover:text-gray-700`}
                    title="PDF Kapat"
                  >
                    <X size={22} />
                  </button>
                )}
              </>
            )}

            {!isOwner && (
              <button
                onClick={onCloseTab}
                className={`${rightSidebarButtonBase} text-gray-500 hover:bg-gray-100 hover:text-gray-700`}
                title="Tahtayı Kapat"
              >
                <X size={22} />
              </button>
            )}

            <div className="w-8 h-px bg-gray-200 my-1"></div>

            {isOwner && (
              <>
                <button
                  onClick={onAddNew}
                  disabled={totalTabs >= 10}
                  className={`${rightSidebarButtonBase} ${totalTabs >= 10 ? "opacity-30 cursor-not-allowed text-gray-300" : "text-green-600 hover:bg-green-50 hover:text-green-700"}`}
                  title={totalTabs >= 10 ? "En fazla 10 beyaz tahta oluşturabilirsiniz" : "Yeni Tahta"}
                >
                  <Plus size={22} />
                </button>
                <button
                  onClick={onDelete}
                  disabled={!canDelete}
                  className={`${rightSidebarButtonBase} ${!canDelete ? "opacity-30 cursor-not-allowed text-gray-300" : "text-red-500 hover:bg-red-50 hover:text-red-600"}`}
                  title={canDelete ? "Tahtayı Sil" : "İlk tahta silinemez"}
                >
                  <Trash2 size={22} />
                </button>
              </>
            )}

            {checkPermission("whiteboard_share") && isOwner && (
              <>
                <button
                  onClick={isShared ? onViewParticipants : onShare}
                  className={`${rightSidebarButtonBase} ${isShared ? "text-purple-600 hover:bg-purple-50 hover:text-purple-700" : "text-blue-600 hover:bg-blue-50 hover:text-blue-700"}`}
                  title={isShared ? "Katılımcıları Görüntüle" : "Tahtayı Paylaş"}
                >
                  {isShared ? <Users size={22} /> : <Share2 size={22} />}
                </button>
                {isShared && (
                  <button
                    onClick={onStopSharing}
                    className={`${rightSidebarButtonBase} text-red-500 hover:bg-red-50 hover:text-red-600`}
                    title="Paylaşımı Durdur"
                  >
                    <Share2 size={22} />
                  </button>
                )}
              </>
            )}

            <div className="w-8 h-px bg-gray-200 my-1"></div>

            <button
              onClick={onToggleFullscreen}
              className={`${rightSidebarButtonBase} ${isFullscreen ? "bg-indigo-600 text-white shadow-lg shadow-indigo-200 hover:bg-indigo-700" : "text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700"}`}
              title={isFullscreen ? "Tam Ekrandan Çık" : "Tam Ekran"}
            >
              {isFullscreen ? <Minimize2 size={22} /> : <Maximize2 size={22} />}
            </button>
          </div>
        </div>
      </div>

      <PdfSelectModal
        isOpen={isPdfSelectModalOpen}
        onClose={() => setIsPdfSelectModalOpen(false)}
        onSelect={handlePdfSelect}
        container={fullscreenContainer}
      />
    </div>
  );
};

const Whiteboard = () => {
  const navigate = useNavigate();
  const { showAlert, showToast } = useModal();
  const { user } = useAuth();
  const [tabs, setTabs] = useState<TabItemProps[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [whiteboardDataMap, setWhiteboardDataMap] = useState<Map<string, WhiteboardData>>(new Map());
  const [socket, setSocket] = useState<Socket | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [participantsModalOpen, setParticipantsModalOpen] = useState(false);
  const [currentWhiteboardId, setCurrentWhiteboardId] = useState<string | null>(null);
  const [currentWhiteboardName, setCurrentWhiteboardName] = useState<string>("");
  const [shareLoading, setShareLoading] = useState(false);
  const [fullscreenContainer, setFullscreenContainer] = useState<HTMLElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);

  const canvasRefsMap = useRef<Map<string, HTMLCanvasElement>>(new Map());

  useEffect(() => {
    if (!checkPermission("whiteboard_view")) {
      navigate("/dashboard");
      return;
    }

    let socketUrl = "";
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

    if (apiBaseUrl) {
      const cleanedUrl = apiBaseUrl.replace(/\/api\/?$/, "").replace(/\/+$/, "");

      try {
        const urlObj = new URL(cleanedUrl);

        if (urlObj.host) {
          socketUrl = cleanedUrl;
        }
      } catch {
      }
    }

    if (!socketUrl) {
      const protocol = window.location.protocol === "https:" ? "https:" : "http:";
      socketUrl = `${protocol}//${window.location.host}`;
    }

    const newSocket = io(socketUrl, {
      path: "/whiteboard-socket",
      transports: ["websocket", "polling"],
    });

    newSocket.on("connect", () => {
      if (user?.id) {
        newSocket.emit("register-user", { userId: user.id });
      }
    });

    newSocket.on("disconnect", () => {
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [navigate, user?.id]);

  const getWhiteboardData = (tabId: string): WhiteboardData => {
    if (!whiteboardDataMap.has(tabId)) {
      return {
        history: [],
        historyStep: -1,
        isOwner: true,
        isShared: false,
        myPermissions: { canDraw: true, canErase: true },
      };
    }
    return whiteboardDataMap.get(tabId)!;
  };

  const updateWhiteboardData = (tabId: string, data: WhiteboardData) => {
    setWhiteboardDataMap((prev) => {
      const newMap = new Map(prev);
      newMap.set(tabId, data);
      return newMap;
    });
  };

  const loadWhiteboards = async () => {
    try {
      const response = await ApiService.getWhiteboards({ limit: 100 });

      const whiteboards = response?.data || [];

      const allTabs: TabItemProps[] = [];

      whiteboards.forEach((wb: any, index: number) => {
        const isOwner = wb.isOwner !== false;
        const ownerName = wb.user ? `${wb.user.firstName || ""} ${wb.user.lastName || ""}`.trim() : "";

        let tabTitle = wb.name || `Beyaz Tahta ${index + 1}`;
        if (!isOwner && ownerName) {
          tabTitle = `Paylaşılan Tahta - ${ownerName}`;
        } else if (isOwner && wb.isShared) {
          tabTitle = "Paylaşılan Tahta";
        }

        allTabs.push({
          type: "custom",
          title: tabTitle,
          path: wb.id,
          tabId: wb.id,
          customComponent: <div />,
        });

        const existingData = whiteboardDataMap.get(wb.id);

        updateWhiteboardData(wb.id, {
          id: wb.id,
          name: wb.name,
          canvasData: existingData?.canvasData,
          history: existingData?.history || [],
          historyStep: existingData?.historyStep ?? -1,
          pdfPages: existingData?.pdfPages || wb.pdfPages,
          currentPdfPage: existingData?.currentPdfPage || wb.currentPdfPage,
          pdfFileId: existingData?.pdfFileId || wb.pdfFileId,
          isOwner: isOwner,
          isShared: wb.isShared || false,
          sharedBy: !isOwner ? wb.user : undefined,
          myPermissions: wb.myPermissions || (isOwner ? { canDraw: true, canErase: true } : { canDraw: false, canErase: false }),
          isSharingStopped: existingData?.isSharingStopped || false,
          stoppedByOwnerName: existingData?.stoppedByOwnerName || '',
        });
      });

      if (allTabs.length === 0) {
        const newWb = await ApiService.createWhiteboard({ name: "Beyaz Tahta 1" });
        allTabs.push({
          type: "custom",
          title: newWb.name || "Beyaz Tahta 1",
          path: newWb.id,
          tabId: newWb.id,
          customComponent: <div />,
        });

        updateWhiteboardData(newWb.id, {
          id: newWb.id,
          name: newWb.name,
          history: [],
          historyStep: -1,
          isOwner: true,
          isShared: false,
          myPermissions: { canDraw: true, canErase: true },
        });
      }

      setTabs(allTabs);
    } catch (error) {
      const initialId = `wb-local-${Date.now()}`;
      setTabs([
        {
          type: "custom",
          title: "Beyaz Tahta 1",
          path: initialId,
          tabId: initialId,
          customComponent: <div />,
        },
      ]);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('tab')) {
      params.delete('tab');
      const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
    loadWhiteboards();
  }, []);

  useEffect(() => {
    if (!socket) return;

    const handleWhiteboardShared = (data: { whiteboard?: { id?: string; myPermissions?: { canDraw: boolean } } }) => {
      const sharedWhiteboardId = data?.whiteboard?.id;

      if (sharedWhiteboardId && whiteboardDataMap.has(sharedWhiteboardId)) {
        const existingData = whiteboardDataMap.get(sharedWhiteboardId);
        if (existingData) {
          updateWhiteboardData(sharedWhiteboardId, {
            ...existingData,
            isShared: true,
            isSharingStopped: false,
            stoppedByOwnerName: undefined,
            myPermissions: data?.whiteboard?.myPermissions || existingData.myPermissions,
          } as WhiteboardData);

          if (existingData.isSharingStopped && user?.id) {
            socket.emit("join-whiteboard", {
              whiteboardId: sharedWhiteboardId,
              userId: user.id,
              userName: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
            });
          }

          return;
        }
      }

      loadWhiteboards();
    };

    socket.on("whiteboard-shared", handleWhiteboardShared);

    return () => {
      socket.off("whiteboard-shared", handleWhiteboardShared);
    };
  }, [socket, whiteboardDataMap, user]);

  const addNewTab = async () => {
    const ownedTabs = tabs.filter((t) => {
      const data = whiteboardDataMap.get(t.tabId || "");
      return data?.isOwner !== false;
    });

    if (ownedTabs.length >= 10) {
      showAlert({
        title: "Maksimum Sayıya Ulaşıldı",
        message: "En fazla 10 beyaz tahta oluşturabilirsiniz.",
        type: "warning",
        buttonText: "Tamam",
      });
      return;
    }

    try {
      const newWb = await ApiService.createWhiteboard({ name: `Beyaz Tahta ${ownedTabs.length + 1}` });
      const newTab: TabItemProps = {
        type: "custom",
        title: newWb.name,
        path: newWb.id,
        tabId: newWb.id,
        customComponent: <div />,
      };

      updateWhiteboardData(newWb.id, {
        id: newWb.id,
        name: newWb.name,
        history: [],
        historyStep: -1,
        isOwner: true,
        isShared: false,
        myPermissions: { canDraw: true, canErase: true },
      });

      setTabs((prevTabs) => [...prevTabs, newTab]);

      setTimeout(() => {
        navigate(`?tab=${newWb.id}`, { replace: true });
      }, 0);

      showToast("Yeni tahta oluşturuldu", "success");
    } catch (error) {
      showToast("Tahta oluşturulamadı", "error");
    }
  };

  const deleteTab = async (tabId: string) => {
    const tabData = whiteboardDataMap.get(tabId);
    if (tabData?.isOwner === false) {
      showToast("Sadece kendi oluşturduğunuz tahtaları silebilirsiniz", "error");
      return;
    }

    const tabIndex = tabs.findIndex((tab) => tab.tabId === tabId);

    try {
      if (tabData?.id) {
        await ApiService.deleteWhiteboard(tabData.id);
      }

      setWhiteboardDataMap((prev) => {
        const newMap = new Map(prev);
        newMap.delete(tabId);
        return newMap;
      });

      await loadWhiteboards();

      if (tabIndex > 0 && tabs.length > 1) {
        const previousTab = tabs[tabIndex - 1];
        setTimeout(() => {
          navigate(`?tab=${previousTab.tabId}`, { replace: true });
        }, 0);
      }

      showToast("Tahta silindi", "success");
    } catch (error) {
      showToast("Tahta silinemedi", "error");
    }
  };

  const handleShare = (tabId: string, tabName: string) => {
    setCurrentWhiteboardId(tabId);
    setCurrentWhiteboardName(tabName);
    setShareModalOpen(true);
  };

  const handleViewParticipants = (tabId: string, tabName: string) => {
    setCurrentWhiteboardId(tabId);
    setCurrentWhiteboardName(tabName);
    setParticipantsModalOpen(true);
  };

  const handleShareSubmit = async (formData: WhiteboardShareFormData) => {
    if (!currentWhiteboardId) return;

    setShareLoading(true);
    try {
      const participants: any[] = [];

      if (formData.type === "USER") {
        formData.selectedUsers.forEach((item) => {
          participants.push({
            type: "USER",
            userId: item.id,
            canDraw: item.canDraw,
            canErase: item.canErase,
          });
        });
      } else {
        formData.selectedDepartments.forEach((item) => {
          participants.push({
            type: "DEPARTMENT",
            departmentId: item.id,
            canDraw: item.canDraw,
            canErase: item.canErase,
          });
        });
      }

      await ApiService.shareWhiteboard(currentWhiteboardId, participants);

      const data = whiteboardDataMap.get(currentWhiteboardId);
      if (data) {
        updateWhiteboardData(currentWhiteboardId, { ...data, isShared: true });
      }

      setShareModalOpen(false);
      showToast("Tahta başarıyla paylaşıldı", "success");
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Tahta paylaşılamadı";
      showAlert({
        title: "Paylaşım Hatası",
        message: errorMessage,
        type: "error",
        buttonText: "Tamam",
      });
    } finally {
      setShareLoading(false);
    }
  };

  const handleStopSharing = async (tabId: string) => {
    const tabData = whiteboardDataMap.get(tabId);
    if (!tabData?.id) return;

    try {
      await ApiService.stopWhiteboardSharing(tabData.id);

      updateWhiteboardData(tabId, { ...tabData, isShared: false });
      showToast("Paylaşım durduruldu", "success");
    } catch (error) {
      showToast("Paylaşım durdurulamadı", "error");
    }
  };

  const closeTab = (tabId: string) => {
    const tabIndex = tabs.findIndex((tab) => tab.tabId === tabId);

    setTabs((prevTabs) => {
      const newTabs = prevTabs.filter((tab) => tab.tabId !== tabId);
      return newTabs;
    });

    setWhiteboardDataMap((prev) => {
      const newMap = new Map(prev);
      newMap.delete(tabId);
      return newMap;
    });

    if (tabIndex > 0 && tabs.length > 1) {
      const previousTab = tabs[tabIndex - 1];
      setTimeout(() => {
        navigate(`?tab=${previousTab.tabId}`, { replace: true });
      }, 0);
    } else if (tabs.length > 1) {
      const nextTab = tabs[1];
      setTimeout(() => {
        navigate(`?tab=${nextTab?.tabId}`, { replace: true });
      }, 0);
    }
  };

  useEffect(() => {
    if (refreshKey > 0) {
      addNewTab();
    }
  }, [refreshKey]);

  useEffect(() => {
    if (tabs.length > 0) {
      setTabs((prevTabs) =>
        prevTabs.map((tab, index) => {
          const wbData = getWhiteboardData(tab.tabId!);
          const isOwner = wbData.isOwner !== false;
          const ownedTabs = prevTabs.filter((t) => {
            const d = whiteboardDataMap.get(t.tabId || "");
            return d?.isOwner !== false;
          });

          let updatedTitle = tab.title;
          if (wbData.isSharingStopped && wbData.stoppedByOwnerName) {
            updatedTitle = `Paylaşımı durduruldu - ${wbData.stoppedByOwnerName}`;
          }

          return {
            ...tab,
            title: updatedTitle,
            customComponent: (
              <WhiteboardCanvas
                key={tab.tabId}
                tabId={tab.tabId!}
                whiteboardId={wbData.id}
                onAddNew={() => setRefreshKey((k) => k + 1)}
                onDelete={() => deleteTab(tab.tabId!)}
                canDelete={isOwner && ownedTabs.length > 1}
                whiteboardData={wbData}
                onDataChange={(data) => updateWhiteboardData(tab.tabId!, data)}
                totalTabs={ownedTabs.length}
                isOwner={isOwner}
                isShared={wbData.isShared || false}
                sharedBy={wbData.sharedBy}
                myPermissions={wbData.myPermissions || { canDraw: true, canErase: true }}
                onShare={() => handleShare(tab.tabId!, wbData.name || tab.title)}
                onViewParticipants={() => handleViewParticipants(tab.tabId!, wbData.name || tab.title)}
                onStopSharing={() => handleStopSharing(tab.tabId!)}
                onCloseTab={() => closeTab(tab.tabId!)}
                isSharingStopped={wbData.isSharingStopped || false}
                stoppedByOwnerName={wbData.stoppedByOwnerName || ''}
                socket={socket}
                onCanvasRef={(tabId, canvas) => {
                  if (canvas) {
                    canvasRefsMap.current.set(tabId, canvas);
                  } else {
                    canvasRefsMap.current.delete(tabId);
                  }
                }}
                onFullscreenContainerChange={setFullscreenContainer}
                onToggleFullscreen={toggleFullscreen}
                isFullscreen={isFullscreen}
                fullscreenContainer={fullscreenContainer}
              />
            ),
          };
        })
      );
    }
  }, [whiteboardDataMap, tabs.length, socket, isFullscreen, fullscreenContainer]);

  const toggleFullscreen = () => {
    if (!fullscreenContainerRef.current) return;

    if (!document.fullscreenElement) {
      fullscreenContainerRef.current.requestFullscreen().then(() => {
        setIsFullscreen(true);
        setFullscreenContainer(fullscreenContainerRef.current);
      }).catch((err) => {
        console.error("Tam ekran açılamadı:", err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
        setFullscreenContainer(null);
      }).catch((err) => {
        console.error("Tam ekrandan çıkılamadı:", err);
      });
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isNowFullscreen);
      setFullscreenContainer(isNowFullscreen ? fullscreenContainerRef.current : null);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  return (
    <div
      ref={fullscreenContainerRef}
      className={`bg-gray-50 overflow-hidden flex flex-col ${isFullscreen ? 'h-screen' : 'h-[88vh]'}`}
    >
      <div className={isFullscreen ? 'h-full flex flex-col [&>div]:h-full [&>div]:flex [&>div]:flex-col' : ''}>
        <TabPage title="Beyaz Tahta" tabs={tabs} />
      </div>

      <WhiteboardShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        onSubmit={handleShareSubmit}
        isLoading={shareLoading}
        title="Tahtayı Paylaş"
        container={fullscreenContainer}
      />

      <WhiteboardParticipantsModal
        isOpen={participantsModalOpen}
        onClose={() => setParticipantsModalOpen(false)}
        whiteboardId={currentWhiteboardId || ""}
        whiteboardName={currentWhiteboardName}
        onParticipantUpdate={() => loadWhiteboards()}
        container={fullscreenContainer}
      />
    </div>
  );
};

export default Whiteboard;
